<template>
  <div class="cycle-page">
    <section class="toolbar">
      <div class="toolbar__grid">
        <div class="date-block">
          <el-date-picker
            v-model="tradeDate"
            type="date"
            size="mini"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :clearable="false"
            @change="onTradeDateChange"
          />
        </div>
        <div class="range-stack">
          <div v-for="group in pathGroups" :key="group.mode" class="field" :class="{ 'is-driving': pathMode === group.mode }">
            <span>{{ group.label }}</span>
            <div class="seg-wrap">
              <div class="seg" :class="{ 'is-on': pathMode === group.mode }" :style="segStyle(group.mode)">
                <i class="seg__thumb" :style="thumbStyle(group.mode)" />
                <button
                  v-for="item in optionsFor(group.mode)"
                  :key="group.mode + '-' + item.id"
                  type="button"
                  :class="{ 'is-active': pathMode === group.mode && chartKey(group.mode) === item.id }"
                  @click="pickChart(group.mode, item.id)"
                >{{ item.label }}</button>
              </div>
              <button
                v-if="group.mode === 'peak'"
                type="button"
                class="match-under"
                :disabled="!poolPicked.length"
                @click="openPoolMatch"
              >概念匹配</button>
            </div>
          </div>
        </div>
        <div class="date-nav">
          <button type="button" :disabled="dateNavLoading || !tradeDate" @click="goPrevTradeDay">昨日</button>
          <button type="button" :disabled="dateNavLoading" @click="goTodayTradeDay">今日</button>
          <button type="button" :disabled="dateNavLoading || !tradeDate" @click="goNextTradeDay">下一日</button>
        </div>
        <div class="toolbar__concept">
          <span>主概念</span>
          <div class="concept-save">
            <el-select
              v-model="conceptDraft"
              size="mini"
              filterable
              multiple
              collapse-tags
              clearable
              allow-create
              default-first-option
              placeholder="选择或输入"
            >
              <el-option v-for="name in activeConceptOptions" :key="name" :label="name" :value="name" />
            </el-select>
            <el-radio-group v-model="conceptMatch" size="mini" class="concept-match">
              <el-radio-button label="or">OR</el-radio-button>
              <el-radio-button label="and">AND</el-radio-button>
            </el-radio-group>
            <el-button size="mini" type="primary" :loading="conceptSaving" @click="saveConceptAndPick">保存</el-button>
            <el-button size="mini" type="warning" plain :loading="conceptSaving" @click="saveKingdom">保存王国</el-button>
          </div>
        </div>
      </div>
    </section>

    <div class="cycle-body">
      <div class="cycle-main" :class="{ 'is-full': isTrial || !sideOpen }">
        <LifecycleCharts
          ref="charts"
          :stocks="chartStocks"
          :axis-labels="axisLabels"
          :path-mode="pathMode"
          :wave-axis-labels="pathMode === 'peak' ? peakDateList : waveDateList"
          :range-key="rangeKey"
          :source-id="sourceId"
          :show-events="showEvents"
          :show-board-labels="showBoardLabels"
          :active-code="activeCode"
          :active-day="activeDay"
          :day-marks="dayMarks"
          :board-stocks="boardLeaderStocks"
          :date-slot="isTrial ? 76 : 0"
          :finals-by-date="isTrial ? finalsByDate : {}"
          :hit-codes="hitCodes"
          :hit-color="activeStock ? activeStock.color : ''"
          :day-expand="dayExpandRows"
          :volume-marks="volumeMarksByCode"
          :trade-date="tradeDate"
          :kingdom-cards="displayKingdomCards"
          :pool-leaders="poolLeaderCards"
          :pool-picked="poolPickedSet"
          :show-pool-match="poolPicked.length > 0"
          @toggle-pool="togglePoolPick"
          @open-match="openPoolMatch"
          @select-stock="selectStock"
          @select-day="selectDay"
          @open-kingdom="focusSavedKingdom"
          @delete-kingdom="deleteKingdom"
          @show-cycle="showCycleChart"
        />

        <el-dialog
          :visible.sync="kingdomOpen"
          append-to-body
          width="96%"
          top="2vh"
          custom-class="kingdom-dialog"
          :title="kingdomTitle"
          @opened="redrawKingdom"
        >
          <kingdom-panel
            v-if="kingdomOpen"
            ref="kingdomPanel"
            :dates="kingdomDates"
            :king="kingdomKing"
            :peers="kingdomPeers"
            :marks="kingdomMarks"
            :catchups="kingdomCatchups"
            :day-marks="dayMarks"
            :loading="kingdomLoading"
            :note="kingdomNote"
            :saving="kingdomSaving"
            :trade-date="tradeDate"
            :concepts="kingdomConceptList"
            :tabs="kingdomCards"
            @switch-kingdom="focusSavedKingdom"
            @save-hidden="saveKingdomRoster"
            @add-peers="addKingdomPeers"
          />
        </el-dialog>

        <section v-show="showStockList && !isTrial" class="table-card">
          <div class="table-head">
            <div class="table-title">个股生命周期对比</div>
            <el-select v-model="roleFilter" size="mini" class="role-filter" placeholder="角色">
              <el-option label="全部角色" value="" />
              <el-option v-for="role in roleOptions" :key="role" :label="role" :value="role" />
            </el-select>
            <span class="table-note">评分列固定显示「待接入」。演示数据。</span>
          </div>
          <el-table
            :data="pagedRows"
            size="mini"
            border
            highlight-current-row
            :row-class-name="rowClass"
            @row-click="onRowClick"
            @sort-change="onSort"
          >
            <el-table-column label="序号" width="54" align="center">
              <template slot-scope="{ $index }">{{ (page - 1) * pageSize + $index + 1 }}</template>
            </el-table-column>
            <el-table-column prop="name" label="股票名称" min-width="96" sortable="custom" />
            <el-table-column prop="code" label="代码" width="84" sortable="custom" />
            <el-table-column prop="role" label="角色" width="84" sortable="custom">
              <template slot-scope="{ row }">
                <el-tag size="mini" effect="plain">{{ row.role }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="totalReturn" label="总涨幅" width="88" sortable="custom" align="right">
              <template slot-scope="{ row }"><span class="up">{{ signed(displayReturn(row)) }}</span></template>
            </el-table-column>
            <el-table-column prop="currentStage" label="当前阶段" width="100" sortable="custom">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="stageTag(row.currentStage)">{{ row.currentStage }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="maxBoard" label="最高板" width="80" sortable="custom" align="center">
              <template slot-scope="{ row }">{{ displayMaxBoard(row) }}B</template>
            </el-table-column>
            <el-table-column prop="currentBoard" label="当前板位" width="88" sortable="custom" align="center">
              <template slot-scope="{ row }">{{ displayCurrentBoard(row) }}B</template>
            </el-table-column>
            <el-table-column prop="stagnationDay" label="停滞日" width="76" sortable="custom" align="center">
              <template slot-scope="{ row }">{{ formatDayTag(row.stagnationDay) }}</template>
            </el-table-column>
            <el-table-column prop="agingDay" label="衰老日" width="76" sortable="custom" align="center">
              <template slot-scope="{ row }">{{ formatDayTag(row.agingDay) }}</template>
            </el-table-column>
            <el-table-column prop="volumeHighCount" label="高位爆量" width="88" sortable="custom" align="center" />
            <el-table-column prop="volumeBreakCount" label="断板爆量" width="88" sortable="custom" align="center" />
            <el-table-column prop="nextOpenLowCount" label="次日低开" width="88" sortable="custom" align="center" />
            <el-table-column prop="nextDeepCount" label="次日深水" width="88" sortable="custom" align="center" />
            <el-table-column prop="nextRepairCount" label="次日修复" width="88" sortable="custom" align="center" />
            <el-table-column label="生命周期评分" width="110" align="center">
              <template>待接入</template>
            </el-table-column>
            <el-table-column label="操作" width="72" align="center">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" @click.stop="selectStock(row.code)">查看</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="pager">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :page-sizes="[4, 5, 10]"
              :page-size="pageSize"
              :current-page="page"
              :total="sortedRows.length"
              @current-change="page = $event"
              @size-change="onPageSize"
            />
          </div>
        </section>
      </div>

      <aside v-if="!isTrial" v-show="sideOpen" class="cycle-side">
        <section class="side-card">
          <div class="side-head">
            <div>
              <div class="side-title">生命周期诊断</div>
              <div class="side-sub">{{ activeStock ? `${activeStock.name} ${activeStock.code}` : '未选择' }} · 演示</div>
            </div>
            <button type="button" class="side-toggle" @click="toggleSide">收起</button>
          </div>
          <div v-if="activeStock" class="diag-metrics">
            <div>
              <span>龙头总涨幅</span>
              <b class="up">{{ signed(displayReturn(activeStock)) }}</b>
            </div>
            <div>
              <span>当前阶段</span>
              <el-tag size="mini" :type="stageTag(activeStock.currentStage)" effect="dark">{{ activeStock.currentStage }}</el-tag>
            </div>
            <div>
              <span>首板日</span>
              <b class="blue">{{ formatDayTag(activeStock.stagnationDay) }}</b>
            </div>
            <div>
              <span>衰老起点</span>
              <b class="red">{{ formatDayTag(activeStock.agingDay) }}</b>
            </div>
          </div>
          <el-radio-group v-model="detailTab" size="mini" class="detail-tabs">
            <el-radio-button label="overview">生命周期总览</el-radio-button>
            <el-radio-button label="events">关键事件</el-radio-button>
            <el-radio-button label="next">次日反馈</el-radio-button>
            <el-radio-button label="base">基础信息</el-radio-button>
          </el-radio-group>

          <div v-if="activeStock && detailTab === 'overview'" class="judge-grid">
            <div class="judge">
              <div class="judge__title judge__title--orange"><i class="el-icon-warning-outline" /> 停滞判断</div>
              <ul>
                <li v-for="(line, index) in activeStock.stagnationPoints" :key="'s' + index">{{ line }}</li>
              </ul>
            </div>
            <div class="judge">
              <div class="judge__title judge__title--red"><i class="el-icon-warning" /> 衰老判断</div>
              <ul>
                <li v-for="(line, index) in activeStock.agingPoints" :key="'a' + index">{{ line }}</li>
              </ul>
            </div>
            <div class="judge">
              <div class="judge__title">周期状态</div>
              <el-tag :type="statusTag(activeStock.cycleStatus)">{{ activeStock.cycleStatus }}</el-tag>
              <p>{{ activeStock.statusNote }}</p>
            </div>
          </div>
          <div v-else-if="activeStock && detailTab === 'events'" class="detail-list">
            <div v-for="event in stockEvents" :key="event.eventId" class="detail-item" @click="selectDay(event.dayIndex)">
              <b :style="{ color: eventMeta(event.eventType).color }">{{ eventMeta(event.eventType).name }}</b>
              <span>D{{ event.dayIndex }} · {{ event.boardHeight }}B</span>
              <p>{{ event.eventDescription }}</p>
            </div>
            <div v-if="!stockEvents.length" class="empty">这只演示股没有事件</div>
          </div>
          <div v-else-if="activeStock && detailTab === 'next'" class="detail-list">
            <div v-for="event in stockEvents" :key="'n' + event.eventId" class="detail-item">
              <b>D{{ event.dayIndex }} {{ eventMeta(event.eventType).name }}</b>
              <span>开 {{ signed(event.nextDayOpenReturn) }} · 低 {{ signed(event.nextDayLowReturn) }} · 收 {{ signed(event.nextDayCloseReturn) }}</span>
              <p>修复状态：{{ event.nextDayRepairStatus }}</p>
            </div>
          </div>
          <div v-else-if="activeStock" class="base-grid">
            <div><span>名称</span>{{ activeStock.name }}</div>
            <div><span>代码</span>{{ activeStock.code }}</div>
            <div><span>王国</span>{{ activeStock.theme }}</div>
            <div><span>角色</span>{{ activeStock.role }}</div>
            <div><span>D0</span>{{ d0Label }}</div>
            <div><span>周期</span>{{ cycle.cycleName }}</div>
          </div>
        </section>

        <section class="side-card">
          <div class="side-title">关键节点数据</div>
          <el-table
            :data="visibleKeyNodes"
            size="mini"
            border
            highlight-current-row
            :row-class-name="nodeRowClass"
            @row-click="onNodeClick"
          >
            <el-table-column prop="tradeDate" label="日期" width="78" />
            <el-table-column prop="stage" label="阶段" width="64">
              <template slot-scope="{ row }">
                <span :class="'stage-text stage-text--' + stageKey(row.stage)">{{ row.stage }}</span>
              </template>
            </el-table-column>
            <el-table-column label="累计涨幅" width="78" align="right">
              <template slot-scope="{ row }"><span class="up">{{ signed(row.cumulativeReturn) }}</span></template>
            </el-table-column>
            <el-table-column label="连板高度" width="72" align="center">
              <template slot-scope="{ row }">{{ row.boardHeight }}B</template>
            </el-table-column>
            <el-table-column prop="amount" label="成交额(亿)" width="84" align="right" />
            <el-table-column label="换手率" width="70" align="right">
              <template slot-scope="{ row }">{{ row.turnover.toFixed(1) }}%</template>
            </el-table-column>
            <el-table-column label="关键事件" min-width="88">
              <template slot-scope="{ row }">
                <el-tag size="mini" effect="plain">{{ row.event }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </section>

        <section class="side-card">
          <div class="side-title">事件标记说明</div>
          <div v-for="item in legendTypes" :key="item.type" class="legend-row">
            <i :class="item.icon" :style="{ color: item.color }" />
            <b>{{ item.name }}</b>
            <span>{{ item.desc }}</span>
          </div>
        </section>

        <section class="side-card">
          <div class="side-title">图表使用说明</div>
          <ol class="usage">
            <li>本页只展示演示配置里的兴起、扩张、停滞、衰老色块，不根据天数自动判定阶段。</li>
            <li>停滞、衰老文案来自每只股票自己的演示说明，不是计算结果。</li>
            <li>断板当天连板高度记为 0，后面的板数从新一段重新数。</li>
            <li>10日 / 15日 / 20日只是观察窗口；全周期横向滚动，不把全部日期压进一屏。</li>
            <li>点图例可以隐藏个股。点折线、表格或节点行，会切到对应股票和日期。</li>
          </ol>
        </section>
      </aside>
      <button v-if="!isTrial && !sideOpen" type="button" class="side-reopen" @click="toggleSide">生命周期诊断</button>
    </div>
    <el-dialog
      :visible.sync="kingdomSaveOpen"
      title="保存王国"
      width="440px"
      append-to-body
      custom-class="kingdom-save-dialog"
    >
      <div class="kingdom-save">
        <div class="kingdom-save__label">给这个王国起个名字</div>
        <el-input v-model="kingdomSaveName" size="small" placeholder="例如 大业-机器人概念-山东" />
        <div class="kingdom-save__box">
          <div class="kingdom-save__label">总龙：<b v-if="kingdomTotalName">{{ kingdomTotalName }}</b></div>
          <div class="kingdom-save__list">
            <div v-if="!kingdomTotalRows.length" class="kingdom-save__empty">监控池没有股票</div>
            <el-radio-group v-else v-model="kingdomTotalCode" class="kingdom-save__branch">
              <el-radio v-for="row in kingdomTotalRows" :key="'t-' + row.code" :label="row.code">
                <span>{{ row.name }}</span>
                <em>{{ signed(row.ret) }}</em>
              </el-radio>
            </el-radio-group>
          </div>
        </div>
        <div class="kingdom-save__box">
          <div class="kingdom-save__label">分支龙：<b v-if="kingdomBranchName">{{ kingdomBranchName }}</b></div>
          <div class="kingdom-save__list">
            <div v-if="!kingdomBranchRows.length" class="kingdom-save__empty">先在观察池勾选股票</div>
            <el-radio-group v-else v-model="kingdomBranchCode" class="kingdom-save__branch">
              <el-radio v-for="row in kingdomBranchRows" :key="'b-' + row.code" :label="row.code">{{ row.name }}</el-radio>
            </el-radio-group>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="kingdomSaveOpen = false">取消</el-button>
        <el-button size="small" type="primary" :loading="conceptSaving" @click="confirmSaveKingdom">保存</el-button>
      </span>
    </el-dialog>
    <el-dialog
      :visible.sync="poolMatchOpen"
      width="96%"
      top="2vh"
      append-to-body
      title="龙头概念匹配"
      custom-class="pool-match-dialog"
    >
      <kingdom-build-board
        v-if="poolMatchOpen"
        :leaders="poolMatchLeaders"
        :concepts-by-code="poolConceptNames"
        :concept-items-by-code="poolConceptItems"
        :trade-date="tradeDate"
        :kingdoms="poolKingdomTabs"
        :preset-codes="poolPicked"
        :pool-stocks="poolKingOptions"
        :show-rail="false"
        :show-culture="false"
        @ensure-concepts="ensurePoolLeaderConcepts"
        @create-kingdom="onPoolCreateKingdom"
      />
    </el-dialog>
  </div>
</template>

<script>
import LifecycleCharts from './components/LifecycleCharts.vue'
import KingdomPanel from '@/views/CycleLifecycle/components/KingdomPanel.vue'
import KingdomBuildBoard from './components/KingdomBuildBoard.vue'
import stockApi from '@/api/stockApi'
import { formatDate } from '@/utils/index'
import { attachReturnRanks, loadLeaderCommandMonitoring } from '@/services/leaderCommandCenterAdapter'
import { persistKingdomTabs } from '@/utils/lccSharedState'
import { CONCEPT_SOURCE_THS, CONCEPT_SOURCE_KPL, CONCEPT_SOURCE_MERGE } from '@/utils/conceptSource'
import { resolveAnalysisConcepts } from '@/utils/conceptFilter'
import {
  CYCLE_META,
  D0_SCENARIOS,
  D0_SOURCES,
  EVENT_TYPES,
  EVENTS,
  KINGDOM_OPTIONS,
  POOL_OPTIONS,
  RANGE_OPTIONS,
  ROLE_OPTIONS,
  STOCKS,
  applyScenario,
  eventMeta
} from '@/views/CycleLifecycle/mockData'

const DAYE_FIRST_BOARD = '2026-10-16'

function isDayeShares(code, name) {
  return String(code || '').split('.')[0] === '603278' || String(name || '') === '大业股份'
}

export default {
  name: 'CycleChartTest',
  components: { LifecycleCharts, KingdomPanel, KingdomBuildBoard },
  data() {
    return {
      cycle: CYCLE_META,
      allStocks: STOCKS,
      rangeOptions: RANGE_OPTIONS,
      waveOptions: [
        { id: 'd10', label: '10日', lastDay: 10 },
        { id: 'd15', label: '15日', lastDay: 15 },
        { id: 'd20', label: '20日', lastDay: 20 },
        { id: 'd25', label: '25日', lastDay: 25 },
        { id: 'd30', label: '30日', lastDay: 30 },
        { id: 'd40', label: '40日', lastDay: 40 },
        { id: 'all', label: '全周期', lastDay: 40 }
      ],
      pathGroups: [
        { mode: 'wave', label: '周期涨幅' },
        { mode: 'peak', label: '最大涨幅排序' }
      ],
      d0Sources: D0_SOURCES,
      poolOptions: POOL_OPTIONS,
      kingdomOptions: KINGDOM_OPTIONS,
      roleOptions: ROLE_OPTIONS,
      legendTypes: EVENT_TYPES.filter((item) => item.type !== 'volumeHigh'),
      rangeKey: 'd15',
      sourceId: 'firstBoard',
      poolId: 'members',
      kingdomId: 'current',
      manualCodes: ['600825', '002242'],
      showEvents: true,
      showBoardLabels: true,
      showStockList: true,
      activeCode: '600825',
      activeDay: -1,
      detailTab: 'overview',
      roleFilter: '',
      sortProp: 'totalReturn',
      sortOrder: 'descending',
      page: 1,
      pageSize: 5,
      tradeDate: '',
      finalsByDate: {},
      finalsLoading: false,
      conceptDraft: [],
      conceptMatch: 'or',
      conceptSaving: false,
      kingdomOpen: false,
      kingdomFocusCode: '',
      kingdomFocusStock: null,
      kingdomCards: [],
      kingdomGalleryLoading: false,
      kingdomLoading: false,
      kingdomNote: '',
      kingdomPeers: [],
      kingdomExtra: [],
      kingdomCatchups: [],
      kingdomHidden: {},
      kingdomMembers: {},
      kingdomSaving: false,
      poolPicked: [],
      kingdomSaveOpen: false,
      kingdomSaveName: '',
      kingdomSaveKind: 'total',
      kingdomTotalCode: '',
      kingdomBranchCode: '',
      poolMatchOpen: false,
      poolMatchSaving: false,
      poolConceptItems: {},
      poolConceptNames: {},
      poolKingdomTabs: [],
      poolConceptPending: {},
      savedConcepts: {},
      savedNameKeys: {},
      savedKingdomTitles: {},
      savedMatchByCode: {},
      conceptsByCode: {},
      axisDates: [],
      calendarDates: [],
      limitUpByDate: {},
      closesByCode: {},
      opensByCode: {},
      dayMarks: {},
      pathMode: 'wave',
      waveKey: 'd20',
      peakKey: 'd10',
      sideOpen: true,
      dateNavLoading: false,
      monitorItems: [],
      monitorLoading: false,
      burstPayload: null,
      burstLoading: false,
      volumeMarksByCode: {}
    }
  },
  computed: {
    isTrial() {
      return this.$route.name === 'CycleChartTest'
    },
    lastDay() {
      const found = RANGE_OPTIONS.find((item) => item.id === this.rangeKey)
      return found ? found.lastDay : 15
    },
    axisLabels() {
      const dates = Array.isArray(this.calendarDates) ? this.calendarDates : []
      const starts = Object.values(this.startByCode).filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item)).sort()
      const min = starts[0]
      if (!min) return dates.filter((item) => item <= this.tradeDate)
      return dates.filter((item) => item >= min && item <= this.tradeDate)
    },
    waveDateList() {
      return this.recentDates(this.waveKey)
    },
    peakDateList() {
      const dates = this.burstPayload && this.burstPayload.dates
      if (this.pathMode === 'peak' && Array.isArray(dates) && dates.length) return dates
      return this.recentDates(this.peakKey)
    },
    burstStocks() {
      const payload = this.burstPayload
      const dates = payload && payload.dates
      if (!Array.isArray(dates) || !dates.length) return []
      const colors = ['#f25c54', '#3b82f6', '#ec4899', '#8b5cf6', '#22c55e', '#e6a23c', '#14b8a6', '#6366f1', '#f97316', '#84cc16', '#06b6d4', '#a855f7', '#0ea5e9', '#d946ef', '#65a30d']
      const dateIndex = {}
      dates.forEach((date, index) => { dateIndex[date] = index })
      const books = new Map()
      const champs = []
      for (const day of payload.days || []) {
        for (const row of day.top || []) {
          if (!row || !row.code) continue
          champs.push({ date: day.date, ...row })
        }
      }
      champs.sort((a, b) => String(a.date).localeCompare(String(b.date)))
      for (const champ of champs) {
        let book = books.get(champ.code)
        if (!book) {
          book = {
            code: champ.code,
            name: champ.name || champ.code,
            values: dates.map(() => null),
            lastRet: -Infinity,
            lastWin: ''
          }
          books.set(champ.code, book)
        }
        if (champ.name) book.name = champ.name
        if (Array.isArray(champ.tail) && champ.tail.length) book.tail = champ.tail
        for (const point of champ.path || []) {
          const index = dateIndex[point.date]
          const cum = Number(point.cum)
          if (index == null || !Number.isFinite(cum)) continue
          book.values[index] = cum
        }
        const winIndex = dateIndex[champ.date]
        const ret = Number(champ.ret != null ? champ.ret : champ.ret10)
        if (winIndex != null && Number.isFinite(ret)) {
          book.lastRet = ret
          book.lastWin = champ.date
          book.wins = book.wins || []
          book.wins.push({ dayIndex: winIndex, ret10: ret })
        }
      }
      books.forEach((book) => {
        for (const win of book.wins || []) book.values[win.dayIndex] = win.ret10
      })
      const ranked = Array.from(books.values()).sort((a, b) => b.lastRet - a.lastRet || String(a.name).localeCompare(String(b.name), 'zh'))
      const lookback = Number(payload.lookback) || 10
      return ranked.map((book, index) => {
        const burstMarks = (book.wins || []).map((win) => ({
          dayIndex: win.dayIndex,
          kind: 'out',
          label: `${book.name} ${Number(win.ret10).toFixed(2)}%`
        }))
        const points = book.values.map((value, dayIndex) => ({
          dayIndex,
          cumulativeReturn: Number.isFinite(value) ? value : null,
          dailyBoardHeight: null
        }))
        return {
          code: book.code,
          name: book.name,
          role: '全A',
          theme: `近${lookback}日第一`,
          kingdomId: 'alla',
          inPool: true,
          color: colors[index % colors.length],
          currentStage: '爆发力',
          stagnationDay: isDayeShares(book.code, book.name) ? DAYE_FIRST_BOARD.slice(5) : '',
          agingDay: '',
          cycleStatus: '活跃',
          statusNote: `全 A 近 ${lookback} 日涨幅当天第一名，线是这 ${lookback} 个交易日的累计涨幅。`,
          totalReturn: book.lastRet,
          maxBoard: 0,
          currentBoard: 0,
          volumeHighCount: 0,
          volumeBreakCount: 0,
          nextOpenLowCount: 0,
          nextDeepCount: 0,
          nextRepairCount: 0,
          stagnationPoints: [],
          agingPoints: [],
          points,
          wavePoints: points,
          peakPoints: points,
          peakTail: book.tail || [],
          burstMarks,
          keyNodes: []
        }
      })
    },
    boardLeaderStocks() {
      const labels = this.axisLabels
      const dates = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      if (!labels.length || !dates.length) return []
      const colors = ['#f25c54', '#3b82f6', '#22c55e', '#e6a23c', '#8b5cf6', '#14b8a6', '#ec4899', '#6366f1']
      const seen = new Set()
      const books = new Map()
      labels.forEach((date) => {
        this.pickHeightStocks(date).forEach((chosen) => {
        if (!chosen) return
        const streak = this.boardStreak(chosen.code, date, dates)
        if (!streak) return
        const key = `${chosen.code}|${streak.start}`
        if (seen.has(key)) return
        seen.add(key)
        let book = books.get(chosen.code)
        if (!book) {
          book = { code: chosen.code, name: chosen.name, boards: {}, notes: {}, times: {} }
          books.set(chosen.code, book)
        }
        if (chosen.name) book.name = chosen.name
        streak.days.forEach((item) => {
          book.boards[item.date] = item.boards
          book.times[item.date] = item.time || book.times[item.date] || ''
          const openPct = this.nextOpenPct(chosen.code, item.date, dates)
          if (openPct != null) {
            book.notes[item.date] = {
              boards: item.boards,
              pct: openPct,
              plus10: openPct >= 10,
              text: openPct >= 10 ? `次日+10%开 +${openPct.toFixed(1)}%` : `次日开盘 ${openPct > 0 ? '+' : ''}${openPct.toFixed(1)}%`
            }
          }
        })
        })
      })
      return Array.from(books.values()).map((book, index) => {
        const openNotes = labels.map((date) => book.notes[date] || null)
        const points = labels.map((date, dayIndex) => ({
          dayIndex,
          cumulativeReturn: null,
          dailyBoardHeight: Number.isFinite(book.boards[date]) ? book.boards[date] : null
        }))
        const peak = points.reduce((max, point) => Math.max(max, point.dailyBoardHeight || 0), 0)
        let peakTime = '999999'
        labels.forEach((date) => {
          if (book.boards[date] !== peak) return
          const time = this.timeKey(book.times[date])
          if (time < peakTime) peakTime = time
        })
        return {
          code: book.code,
          name: book.name,
          color: colors[index % colors.length],
          points,
          openNotes,
          timeByDate: book.times,
          peakBoard: peak,
          peakTime,
          boardDays: Object.keys(book.boards).sort().map((date) => ({ date, boards: book.boards[date] })),
          volumeMarks: (this.volumeMarksByCode && this.volumeMarksByCode[book.code]) || []
        }
      }).sort((a, b) => b.peakBoard - a.peakBoard || String(a.peakTime).localeCompare(String(b.peakTime)) || String(a.name).localeCompare(String(b.name), 'zh'))
    },
    startByCode() {
      const map = {}
      for (const item of this.monitorItems || []) {
        const code = String(item.code || '').split('.')[0]
        if (!code) continue
        map[code] = this.walkFirstBoard(code, item)
      }
      return map
    },
    monitorStocks() {
      return (this.monitorItems || []).map((item, index) => this.toMonitorStock(item, index))
    },
    scenarioNote() {
      return (D0_SCENARIOS[this.sourceId] || D0_SCENARIOS.firstBoard).note
    },
    d0Label() {
      const found = D0_SOURCES.find((item) => item.id === this.sourceId)
      return found ? found.label : ''
    },
    defaultCode() {
      return this.visibleStocks.length ? this.visibleStocks[0].code : ''
    },
    visibleStocks() {
      if (this.pathMode === 'peak') return this.burstStocks
      return this.monitorStocks
    },
    chartStocks() {
      const stocks = this.visibleStocks
      const code = String(this.activeCode || '').split('.')[0]
      const tokens = this.savedTokens(code)
      const picked = this.poolPickedSet || {}
      let rows = stocks
      if (this.conceptMatchMode(code) === 'and' && (tokens.concepts.length || tokens.nameKeys.length)) {
        rows = stocks.filter((stock) => {
          const short = String(stock.code || '').split('.')[0]
          return short === code || picked[short] || this.stockMatchesSaved(stock, tokens.concepts, tokens.nameKeys, 'and')
        })
      }
      const have = {}
      rows.forEach((stock) => { have[String(stock.code || '').split('.')[0]] = true })
      const extra = []
      ;(this.monitorStocks || []).forEach((stock) => {
        const short = String(stock.code || '').split('.')[0]
        if (picked[short] && !have[short]) extra.push(stock)
      })
      return extra.length ? rows.concat(extra) : rows
    },
    activeStock() {
      return this.visibleStocks.find((stock) => stock.code === this.activeCode) || this.visibleStocks[0] || null
    },
    conceptCodeList() {
      const codes = []
      for (const stock of this.visibleStocks) codes.push(String(stock.code || '').split('.')[0])
      const dates = this.finalsByDate || {}
      Object.keys(dates).forEach((date) => {
        for (const stock of dates[date] || []) codes.push(String(stock.code || '').split('.')[0])
      })
      return [...new Set(codes.filter((code) => /^\d{6}$/.test(code)))]
    },
    activeConceptOptions() {
      const code = String(this.activeCode || '').split('.')[0]
      const list = ((this.conceptsByCode && this.conceptsByCode[code]) || []).slice()
      this.conceptNames(this.savedConcepts[code]).concat(this.conceptNames(this.conceptDraft)).forEach((name) => {
        if (list.indexOf(name) < 0) list.unshift(name)
      })
      return list
    },
    poolPickRows() {
      if (!this.tradeDate) return []
      const calendar = (this.calendarDates || []).filter((date) => date && date <= this.tradeDate)
      const prev = calendar.filter((date) => date < this.tradeDate).slice(-1)[0] || ''
      const rows = (this.monitorStocks || []).map((stock) => {
        const closes = (this.closesByCode && this.closesByCode[stock.code]) || {}
        const todayClose = Number(closes[this.tradeDate])
        const prevClose = Number(closes[prev])
        const ret = Number.isFinite(todayClose) && Number.isFinite(prevClose) && prevClose > 0
          ? Math.round((todayClose / prevClose - 1) * 1000) / 10
          : null
        return { code: stock.code, name: stock.name, color: stock.color, ret }
      })
      rows.sort((a, b) => {
        if (a.ret == null && b.ret == null) return String(a.name).localeCompare(String(b.name), 'zh')
        if (a.ret == null) return 1
        if (b.ret == null) return -1
        return b.ret - a.ret || String(a.name).localeCompare(String(b.name), 'zh')
      })
      return rows
    },
    dayExpandRows() {
      if (this.pathMode !== 'wave') return []
      return this.poolPickRows
    },
    kingdomReturnRows() {
      return (this.monitorStocks || []).map((stock) => {
        const code = String(stock.code || '').split('.')[0]
        return { code, name: stock.name || code, ret: this.d20Return(code) }
      }).filter((row) => /^\d{6}$/.test(row.code))
    },
    kingdomTotalName() {
      const row = this.kingdomTotalRows.find((item) => item.code === this.kingdomTotalCode)
      return row ? row.name : ''
    },
    kingdomBranchName() {
      const row = this.kingdomBranchRows.find((item) => item.code === this.kingdomBranchCode)
      return row ? row.name : ''
    },
    kingdomTotalRows() {
      const rows = this.kingdomReturnRows.slice()
      rows.sort((a, b) => {
        if (a.ret == null && b.ret == null) return String(a.name).localeCompare(String(b.name), 'zh')
        if (a.ret == null) return 1
        if (b.ret == null) return -1
        return b.ret - a.ret || String(a.name).localeCompare(String(b.name), 'zh')
      })
      return rows
    },
    kingdomBranchRows() {
      const picked = {}
      const list = this.poolPicked || []
      list.forEach((code) => { picked[code] = true })
      return this.kingdomReturnRows.filter((row) => picked[row.code])
    },
    poolPickedSet() {
      const set = {}
      this.poolPicked.forEach((code) => { set[code] = true })
      return set
    },
    kingdomMembership() {
      const asOf = String(this.tradeDate || '')
      const map = {}
      const add = (code, king) => {
        if (!/^\d{6}$/.test(code) || !/^\d{6}$/.test(king)) return
        const list = map[code] || []
        if (list.indexOf(king) < 0) list.push(king)
        map[code] = list
      }
      this.savedKingdomCodes().forEach((king) => {
        add(king, king)
        const hidden = this.hiddenCodeSet(king)
        const members = (this.kingdomMembers && this.kingdomMembers[king]) || []
        members.forEach((item) => {
          const code = String((item && item.code) || '').split('.')[0]
          const savedDate = String((item && item.savedDate) || '')
          if (!/^\d{6}$/.test(code) || code === king || hidden[code]) return
          if (savedDate && asOf && savedDate > asOf) return
          add(code, king)
        })
      })
      return map
    },
    loosePoolNames() {
      const member = this.kingdomMembership
      return (this.poolPickRows || []).filter((row) => !(member[row.code] || []).length).map((row) => this.poolNameTag(row, member))
    },
    poolLeaderCards() {
      const byCode = {}
      ;(this.monitorItems || []).forEach((item) => {
        const code = String((item && item.code) || '').split('.')[0]
        if (code) byCode[code] = item
      })
      const cards = (this.loosePoolNames || []).map((row) => byCode[row.code]).filter(Boolean)
      return attachReturnRanks(cards)
    },
    displayKingdomCards() {
      const member = this.kingdomMembership
      return (this.kingdomCards || []).map((card) => {
        const pool = this.monitorPoolSet()
        const codes = []
        Object.keys(member).forEach((code) => {
          if (pool[code] && (member[code] || []).indexOf(card.code) >= 0) codes.push(code)
        })
        const members = (this.kingdomMembers && this.kingdomMembers[card.code]) || []
        const order = [card.code].concat(members.map((item) => String((item && item.code) || '').split('.')[0]))
        codes.sort((a, b) => {
          const ia = order.indexOf(a)
          const ib = order.indexOf(b)
          if (ia < 0 && ib < 0) return String(a).localeCompare(String(b))
          if (ia < 0) return 1
          if (ib < 0) return -1
          return ia - ib
        })
        const tags = codes.map((code) => {
          const row = (this.poolPickRows || []).find((item) => item.code === code)
          return this.poolNameTag(row || { code, name: this.stockNameOf(code) || code, color: '#909399' }, member)
        })
        return Object.assign({}, card, { tags })
      })
    },
    poolMatchLeaders() {
      const picked = this.poolPickedSet
      return (this.monitorStocks || []).filter((stock) => picked[stock.code]).map((stock) => ({
        code: stock.code,
        name: stock.name,
        leaderId: stock.code
      }))
    },
    poolKingOptions() {
      return (this.monitorStocks || []).map((stock) => ({
        code: stock.code,
        name: stock.name,
        leaderId: stock.code
      }))
    },
    hitCodes() {
      const hits = {}
      const code = String(this.activeCode || '').split('.')[0]
      const tokens = this.savedTokens(code)
      if (tokens.concepts.length || tokens.nameKeys.length) {
        const hidden = this.hiddenCodeSet(code)
        const mode = this.conceptMatchMode(code)
        const mark = (stock) => {
          const short = String((stock && stock.code) || '').split('.')[0]
          if (!short || hidden[short]) return
          if (this.stockMatchesSaved(stock, tokens.concepts, tokens.nameKeys, mode)) hits[short] = true
        }
        this.visibleStocks.forEach((stock) => mark(stock))
        const dates = this.finalsByDate || {}
        Object.keys(dates).forEach((date) => {
          for (const stock of dates[date] || []) mark(stock)
        })
      }
      const picked = this.poolPicked || []
      for (let i = 0; i < picked.length; i += 1) {
        const short = String(picked[i] || '').split('.')[0]
        if (/^\d{6}$/.test(short)) hits[short] = true
      }
      return hits
    },
    kingdomSourceDates() {
      if (this.pathMode === 'wave') return this.waveDateList || []
      return this.axisLabels || []
    },
    kingdomDates() {
      const full = this.pathMode === 'peak' ? (this.peakDateList || []) : this.kingdomSourceDates
      const code = String(((this.kingdomFocusStock && this.kingdomFocusStock.code) || (this.activeStock && this.activeStock.code) || '')).split('.')[0]
      const first = code && this.startByCode ? this.startByCode[code] : ''
      if (!/^\d{4}-\d{2}-\d{2}$/.test(first)) return full
      const sliced = full.filter((date) => date >= first)
      return sliced.length ? sliced : full
    },
    kingdomConceptList() {
      if (this.kingdomFocusCode) {
        return this.conceptNames(this.savedConcepts && this.savedConcepts[this.kingdomFocusCode])
      }
      const code = String(this.activeCode || '').split('.')[0]
      const draft = this.conceptNames(this.conceptDraft)
      const source = draft.length ? draft : this.conceptNames(this.savedConcepts && this.savedConcepts[code])
      return this.splitConceptDraft(code, source).concepts
    },
    kingdomDialogTitle() {
      if (!this.kingdomFocusCode) return '已保存的王国'
      return this.kingdomTitle
    },
    kingdomTitle() {
      const code = String(this.kingdomFocusCode || (this.kingdomKing && this.kingdomKing.code) || '').split('.')[0]
      const saved = code && this.savedKingdomTitles && this.savedKingdomTitles[code]
      if (saved) return saved
      const king = this.kingdomFocusStock || this.activeStock
      const concepts = this.kingdomConceptList
      const name = king ? king.name : '王国'
      return concepts.length ? `${name} · ${concepts.join('、')}` : name
    },
    kingdomKing() {
      const stock = this.kingdomFocusStock || this.activeStock
      const dates = this.kingdomDates
      if (!stock) return null
      const source = this.pathMode === 'peak' && Array.isArray(stock.peakPoints)
        ? stock.peakPoints
        : this.pathMode === 'wave' && Array.isArray(stock.wavePoints)
          ? stock.wavePoints
          : stock.points || []
      const sourceDates = this.kingdomSourceDates
      const indexOf = {}
      sourceDates.forEach((date, index) => { indexOf[date] = index })
      const values = dates.map((date) => {
        const index = indexOf[date]
        if (index == null) return null
        const raw = applyScenario(source[index] && source[index].cumulativeReturn, this.sourceId)
        const n = Number(raw)
        return raw != null && raw !== '' && Number.isFinite(n) ? n : null
      })
      return { name: stock.name, code: String(stock.code || '').split('.')[0], color: stock.color, values }
    },
    kingdomMarks() {
      const king = this.kingdomKing
      const dates = this.kingdomDates
      if (!king) return []
      const marks = (this.volumeMarksByCode && this.volumeMarksByCode[king.code]) || []
      const rows = []
      marks.forEach((mark) => {
        const index = dates.indexOf(mark.date)
        if (index < 0) return
        const y = king.values[index]
        if (!Number.isFinite(y)) return
        const boardsN = Math.floor(Number(mark.boards))
        const high = mark.kind === 'high'
        if (!high && !(boardsN >= 2 && boardsN <= 4)) return
        rows.push({
          date: mark.date,
          y,
          color: high ? '#e53935' : '#e6a23c',
          name: high ? '高位爆量' : '爆量'
        })
      })
      return rows
    },
    stockEvents() {
      if (!this.activeStock) return []
      return EVENTS.filter((item) => item.stockCode === this.activeStock.code && item.dayIndex <= this.visibleLastIndex())
    },
    visibleKeyNodes() {
      if (!this.activeStock) return []
      return this.activeStock.keyNodes
        .filter((node) => node.dayIndex <= this.visibleLastIndex())
        .map((node) => {
          const point = this.activeStock.points[node.dayIndex]
          return {
            ...node,
            tradeDate: this.formatDayIndex(node.dayIndex),
            cumulativeReturn: applyScenario(point.cumulativeReturn, this.sourceId),
            boardHeight: point.dailyBoardHeight
          }
        })
    },
    filteredRows() {
      return this.visibleStocks.filter((stock) => !this.roleFilter || stock.role === this.roleFilter)
    },
    sortedRows() {
      const list = this.filteredRows.slice()
      const prop = this.sortProp
      const dir = this.sortOrder === 'ascending' ? 1 : -1
      if (!prop) return list
      list.sort((a, b) => {
        const left = prop === 'totalReturn' ? this.displayReturn(a) : a[prop]
        const right = prop === 'totalReturn' ? this.displayReturn(b) : b[prop]
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * dir
        return String(left || '').localeCompare(String(right || ''), 'zh') * dir
      })
      return list
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.sortedRows.slice(start, start + this.pageSize)
    }
  },
  watch: {
    visibleStocks() {
      if (!this.visibleStocks.some((stock) => stock.code === this.activeCode)) {
        this.activeCode = this.defaultCode
      }
      this.page = 1
    },
    roleFilter() {
      this.page = 1
    },
    rangeKey() {
      if (this.activeDay > this.lastDay) this.activeDay = -1
    },
    pathMode() {
      this.loadBurstTop()
    },
    poolPickRows(rows) {
      const alive = {}
      const list = rows || []
      for (let i = 0; i < list.length; i += 1) alive[list[i].code] = true
      const next = this.poolPicked.filter((code) => alive[code])
      if (next.length !== this.poolPicked.length) this.poolPicked = next
    },
    peakKey() {
      this.loadBurstTop()
    },
    '$route.name'(name) {
      if (name === 'CycleChartTest') this.loadFinalBoard()
    },
    activeCode() {
      this.syncConceptDraft()
    },
    tradeDate() {
      if (!this.kingdomOpen) return
      this.kingdomExtra = this.savedKingdomMembers()
      this.refreshKingdomPeers()
    },
    conceptCodeList(codes) {
      this.ensureConceptNames(codes)
    }
  },
  mounted() {
    this.loadSavedConcepts()
    this.initTradeDate()
  },
  methods: {
    d20Return(code) {
      const items = this.monitorItems || []
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i]
        if (String((item && item.code) || '').split('.')[0] !== code) continue
        const n = Number(item.returns && item.returns.d20)
        return Number.isFinite(n) ? n : null
      }
      return null
    },
    conceptMatchMode(code) {
      const mode = this.savedMatchByCode && this.savedMatchByCode[code]
      return mode === 'and' ? 'and' : 'or'
    },
    savedTokens(code) {
      return {
        concepts: this.conceptNames(this.savedConcepts && this.savedConcepts[code]),
        nameKeys: this.conceptNames(this.savedNameKeys && this.savedNameKeys[code])
      }
    },
    splitConceptDraft(code, draft) {
      const realList = this.conceptsByCode && this.conceptsByCode[code]
      const realReady = Array.isArray(realList)
      const real = {}
      if (realReady) realList.forEach((name) => { real[name] = true })
      const savedConcepts = {}
      this.conceptNames(this.savedConcepts && this.savedConcepts[code]).forEach((name) => { savedConcepts[name] = true })
      const concepts = []
      const nameKeys = []
      this.conceptNames(draft).forEach((name) => {
        const isConcept = realReady ? !!real[name] : !!savedConcepts[name]
        if (isConcept) concepts.push(name)
        else nameKeys.push(name)
      })
      return { concepts, nameKeys }
    },
    stockMatchesSaved(stock, concepts, nameKeys, mode) {
      const checks = []
      const short = String((stock && stock.code) || '').split('.')[0]
      const owned = (this.conceptsByCode && this.conceptsByCode[short]) || []
      const conceptList = concepts || []
      conceptList.forEach((name) => { checks.push(owned.indexOf(name) >= 0) })
      const stockName = String((stock && stock.name) || '')
      const keyList = nameKeys || []
      keyList.forEach((word) => { checks.push(stockName.indexOf(word) >= 0) })
      if (!checks.length) return false
      return mode === 'and' ? checks.every(Boolean) : checks.some(Boolean)
    },
    conceptNames(value) {
      const list = Array.isArray(value) ? value : [value]
      return [...new Set(list.map((item) => String(item || '').trim()).filter(Boolean))]
    },
    eventMeta,
    visibleLastIndex() {
      if (this.axisLabels.length) return this.axisLabels.length - 1
      return this.lastDay
    },
    formatDayIndex(index) {
      const date = this.axisLabels[index]
      if (!date) return `D${index}`
      return String(date).slice(5)
    },
    formatDayTag(tag) {
      const matched = String(tag || '').match(/^D(\d+)$/)
      if (!matched) return tag || '—'
      return this.formatDayIndex(Number(matched[1]))
    },
    windowReturnOf(item) {
      const returns = (item && item.returns) || {}
      const key = this.rangeKey === 'd10' ? 'd10' : this.rangeKey === 'd20' || this.rangeKey === 'all' ? 'd20' : 'd15'
      const n = Number(returns[key])
      return Number.isFinite(n) ? Math.round(n * 10) / 10 : null
    },
    boardNumber(item, field) {
      const n = Number(item && item[field])
      if (Number.isFinite(n)) return n
      const matched = String((item && item.boardTierLabel) || '').match(/(\d+)/)
      return matched ? Number(matched[1]) : 0
    },
    toMonitorStock(item, index) {
      const colors = ['#f25c54', '#3b82f6', '#ec4899', '#8b5cf6', '#22c55e', '#e6a23c', '#14b8a6', '#6366f1', '#f97316', '#84cc16', '#06b6d4', '#a855f7']
      const labels = this.axisLabels
      const code = String(item.code || '').split('.')[0]
      const firstBoard = this.startByCode[code] || ''
      let startIdx = firstBoard ? labels.findIndex((date) => date >= firstBoard) : -1
      if (firstBoard && startIdx < 0) startIdx = Math.max(labels.length - 1, 0)
      const end = this.windowReturnOf(item)
      const startPct = this.firstBoardPct(code)
      const board = this.boardNumber(item, 'currentBoard')
      const maxBoard = this.boardNumber(item, 'maxBoardAsOf') || board
      const last = Math.max(labels.length - 1, 0)
      const span = Math.max(last - Math.max(startIdx, 0), 1)
      const points = (labels.length ? labels : Array.from({ length: 25 }, (_, i) => i)).map((_, i) => {
        if (startIdx < 0 || i < startIdx) {
          return { dayIndex: i, cumulativeReturn: null, dailyBoardHeight: null }
        }
        const t = (i - startIdx) / span
        return {
          dayIndex: i,
          cumulativeReturn: end == null ? null : Math.round((startPct + (end - startPct) * t) * 10) / 10,
          dailyBoardHeight: board
        }
      })
      return {
        code,
        name: item.name || item.code,
        role: '监控',
        theme: '监控池',
        kingdomId: 'monitor',
        inPool: true,
        color: colors[index % colors.length],
        currentStage: '监控中',
        stagnationDay: firstBoard ? firstBoard.slice(5) : '',
        agingDay: '',
        cycleStatus: '活跃',
        statusNote: firstBoard
          ? `首板 ${firstBoard}。${this.isReentry(item) ? '二次入池，取截止日前 20 个交易日内的第一个涨停。' : '入池日前连续涨停的第一板。'}首板当天 ${startPct}%。`
          : '没有数到首板日。',
        totalReturn: end,
        maxBoard,
        currentBoard: board,
        volumeHighCount: 0,
        volumeBreakCount: 0,
        nextOpenLowCount: 0,
        nextDeepCount: 0,
        nextRepairCount: 0,
        stagnationPoints: [firstBoard ? `首板 ${firstBoard}` : '没有首板日'],
        agingPoints: ['监控池暂无衰老说明'],
        points,
        wavePoints: this.buildWavePoints(code, firstBoard, this.waveDateList),
        peakPoints: this.buildPeakPoints(code, this.axisLabels),
        keyNodes: []
      }
    },
    chartKey(mode) {
      if (mode === 'wave') return this.waveKey
      if (mode === 'peak') return this.peakKey
      return this.rangeKey
    },
    optionsFor(mode) {
      return mode === 'wave' ? this.waveOptions : this.rangeOptions
    },
    segStyle(mode) {
      const count = this.optionsFor(mode).length || 1
      return { gridTemplateColumns: `repeat(${count}, 52px)` }
    },
    pickChart(mode, key) {
      this.pathMode = mode
      if (mode === 'wave') this.waveKey = key
      else if (mode === 'peak') this.peakKey = key
      else this.rangeKey = key
      if (mode === 'peak') this.loadBurstTop()
    },
    async loadBurstTop() {
      if (this.pathMode !== 'peak' || !this.tradeDate) return
      const windowDays = { d10: 10, d15: 15, d20: 20, all: 30 }[this.peakKey] || 10
      const stamp = `${this.tradeDate}|${windowDays}`
      this.burstStamp = stamp
      this.burstLoading = true
      try {
        const data = await stockApi.fetchAllABurstTop({ endDate: this.tradeDate, window: windowDays, lookback: windowDays })
        if (this.burstStamp !== stamp || this.pathMode !== 'peak') return
        this.burstPayload = data && data.ok !== false ? data : null
        const first = this.burstStocks[0]
        if (first && !this.burstStocks.some((item) => item.code === this.activeCode)) this.activeCode = first.code
      } catch (err) {
        if (this.burstStamp === stamp) {
          this.burstPayload = null
          this.$message.error((err && err.message) || '全A涨幅排序加载失败')
        }
      } finally {
        if (this.burstStamp === stamp) this.burstLoading = false
      }
    },
    thumbStyle(mode) {
      const index = Math.max(0, this.optionsFor(mode).findIndex((item) => item.id === this.chartKey(mode)))
      return {
        opacity: this.pathMode === mode ? 1 : 0,
        transform: `translateX(${index * 52}px)`
      }
    },
    recentDates(key) {
      const dates = (this.calendarDates || []).filter((item) => item && item <= this.tradeDate)
      const span = { d10: 10, d15: 15, d20: 20, d25: 25, d30: 30, d40: 40 }[key]
      if (!span) return dates
      return dates.slice(-span)
    },
    buildPeakPoints(code, dates) {
      const closes = (this.closesByCode && this.closesByCode[code]) || {}
      const ordered = Object.keys(closes).sort()
      const indexOf = {}
      ordered.forEach((date, index) => { indexOf[date] = index })
      const span = { d10: 10, d15: 15, d20: 20 }[this.peakKey] || 0
      const firstPos = span ? -1 : ordered.findIndex((date) => dates.indexOf(date) >= 0)
      return (dates || []).map((date, index) => {
        const pos = indexOf[date]
        const close = pos == null ? NaN : Number(closes[date])
        const basePos = span ? pos - span : firstPos
        const base = basePos >= 0 ? Number(closes[ordered[basePos]]) : NaN
        if (pos == null || !Number.isFinite(close) || !Number.isFinite(base) || base === 0) {
          return { dayIndex: index, cumulativeReturn: null, dailyBoardHeight: null }
        }
        return {
          dayIndex: index,
          cumulativeReturn: Math.round((close / base - 1) * 1000) / 10,
          dailyBoardHeight: null
        }
      })
    },
    buildWavePoints(code, firstBoard, dates) {
      const closes = (this.closesByCode && this.closesByCode[code]) || {}
      const ordered = Object.keys(closes).sort()
      const baseDate = firstBoard ? ordered.find((date) => date >= firstBoard) : ordered[0]
      const baseClose = baseDate ? Number(closes[baseDate]) : null
      const startPct = this.firstBoardPct(code)
      const factor = 1 + startPct / 100
      const windowStart = dates[0] || ''
      let carried = null
      if (baseClose) {
        const prior = ordered.filter((date) => date < windowStart && (!firstBoard || date >= firstBoard)).pop()
        const priorClose = prior ? Number(closes[prior]) : NaN
        if (Number.isFinite(priorClose) && priorClose > 0) {
          carried = Math.round((priorClose / baseClose * factor - 1) * 1000) / 10
        }
      }
      return dates.map((date, index) => {
        if (!baseClose || (firstBoard && date < firstBoard)) {
          return { dayIndex: index, cumulativeReturn: null, dailyBoardHeight: null }
        }
        const close = Number(closes[date])
        if (Number.isFinite(close) && close > 0) {
          carried = Math.round((close / baseClose * factor - 1) * 1000) / 10
        }
        return {
          dayIndex: index,
          cumulativeReturn: carried,
          dailyBoardHeight: null
        }
      })
    },
    isReentry(item) {
      return String((item && item.entryReason) || '').indexOf('REENTRY') === 0
    },
    firstBoardPct(code) {
      return /^(300|301|688|689)/.test(String(code || '')) ? 20 : 10
    },
    hasLimitUp(date, code) {
      const day = this.limitUpByDate[date]
      return !!(day && Object.prototype.hasOwnProperty.call(day, code))
    },
    boardsOfLimitUp(item) {
      const raw = String((item && (item.consecutiveLimitUpDays || item.tier)) || '').trim()
      const matched = raw.match(/^(\d+)天(\d+)板$/)
      if (matched) return Number(matched[2]) || 0
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    heightLeaderCodes(byDate, dates) {
      const codes = new Set()
      ;(dates || []).forEach((date) => {
        this.pickHeightStocks(date, byDate).forEach((item) => codes.add(item.code))
      })
      return [...codes]
    },
    limitUpItems(date, source) {
      const day = (source || this.limitUpByDate)[date]
      if (!day) return []
      return Object.keys(day).map((code) => {
        const raw = day[code]
        if (raw && typeof raw === 'object') {
          return { code, boards: Number(raw.boards) || 0, name: raw.name || code, time: String(raw.time || '') }
        }
        return { code, boards: Number(raw) || 0, name: code, time: '' }
      }).filter((item) => item.boards > 0)
    },
    timeKey(time) {
      const digits = String(time || '').replace(/\D/g, '')
      return digits ? digits.padStart(6, '0') : '999999'
    },
    sortByLimitUpTime(items) {
      return items.slice().sort((a, b) => this.timeKey(a.time).localeCompare(this.timeKey(b.time)) || a.code.localeCompare(b.code))
    },
    earliestLimitUp(items) {
      return this.sortByLimitUpTime(items)[0] || null
    },
    pickHeightStocks(date, source) {
      const items = this.limitUpItems(date, source)
      if (!items.length) return []
      const maxBoard = items.reduce((max, item) => Math.max(max, item.boards), 0)
      if (maxBoard <= 3) {
        const first = this.earliestLimitUp(items)
        return first ? [first] : []
      }
      return this.sortByLimitUpTime(items.filter((item) => item.boards === maxBoard))
    },
    boardStreak(code, date, dates) {
      let index = dates.indexOf(date)
      if (index < 0) return null
      while (index > 0 && this.limitUpItems(dates[index - 1]).some((item) => item.code === code)) index -= 1
      const start = index
      let peak = start
      let peakBoard = 0
      const days = []
      for (let i = start; i < dates.length; i += 1) {
        const hit = this.limitUpItems(dates[i]).find((item) => item.code === code)
        if (!hit) break
        days.push({ date: dates[i], boards: hit.boards, time: hit.time })
        if (hit.boards >= peakBoard) {
          peakBoard = hit.boards
          peak = i
        }
      }
      const peakDate = dates[peak]
      return { start: dates[start], peak: peakDate, days: days.filter((item) => item.date <= peakDate) }
    },
    nextOpenPct(code, date, dates) {
      const index = dates.indexOf(date)
      const next = index >= 0 ? dates[index + 1] : ''
      const prevClose = Number((this.closesByCode[code] || {})[date])
      const nextOpen = Number((this.opensByCode[code] || {})[next])
      if (!next || !Number.isFinite(prevClose) || prevClose <= 0 || !Number.isFinite(nextOpen) || nextOpen <= 0) return null
      return Math.round((nextOpen / prevClose - 1) * 1000) / 10
    },
    walkFirstBoard(code, item) {
      if (isDayeShares(code, item && item.name)) return DAYE_FIRST_BOARD
      const entry = String(item.entryDate || item.firstFourBoardDate || item.fourBoardDate || '').slice(0, 10)
      const dates = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      if (this.isReentry(item)) {
        const hits = dates.slice(-20).filter((date) => this.hasLimitUp(date, code))
        if (hits.length) return hits[0]
        return entry
      }
      const upto = dates.filter((date) => !entry || date <= entry)
      let first = ''
      let started = false
      for (let i = upto.length - 1; i >= 0; i--) {
        const date = upto[i]
        const hit = this.hasLimitUp(date, code)
        if (!started) {
          if (!hit) break
          started = true
          first = date
          continue
        }
        if (!hit) break
        first = date
      }
      return first || entry
    },
    async loadMonitoring() {
      if (!this.tradeDate) return
      this.monitorLoading = true
      try {
        const payload = await loadLeaderCommandMonitoring(this.tradeDate)
        const items = (payload && payload.activeMonitoring && payload.activeMonitoring.items) || []
        this.monitorItems = items.filter((item) => item && item.code)
        if (!this.monitorItems.some((item) => String(item.code).split('.')[0] === this.activeCode)) {
          this.activeCode = this.monitorItems.length ? String(this.monitorItems[0].code).split('.')[0] : ''
        }
        this.dropKingdomIfKingLeft()
        this.loadKingdomGallery()
      } catch (err) {
        this.monitorItems = []
        this.$message.error((err && err.message) || '监控池加载失败')
      } finally {
        this.monitorLoading = false
      }
    },
    addCalendarDays(ymd, days) {
      const parts = String(ymd || '').split('-').map((item) => Number(item))
      const date = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1)
      date.setDate(date.getDate() + days)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },
    async initTradeDate() {
      this.dateNavLoading = true
      try {
        const res = await stockApi.resolveHistoryFirstBoardTradeDate(null)
        let tradeDate = (res && res.tradeDate) || formatDate()
        const now = new Date()
        if (now.getHours() < 6 && tradeDate === formatDate(now)) {
          try {
            const prev = await stockApi.prevHistoryFirstBoardTradeDate(tradeDate)
            if (prev && prev.ok && prev.prevTradeDate) tradeDate = prev.prevTradeDate
          } catch (err) {
            /* 上一交易日没取到时仍用当天 */
          }
        }
        this.tradeDate = tradeDate
      } catch (err) {
        this.tradeDate = formatDate()
      } finally {
        this.dateNavLoading = false
      }
      await this.loadMonitoring()
      await this.loadAxisDates()
    },
    async onTradeDateChange() {
      if (!this.tradeDate) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.resolveHistoryFirstBoardTradeDate(this.tradeDate)
        if (res && res.ok && res.tradeDate) this.tradeDate = res.tradeDate
      } catch (err) {
        /* 选中的日期仍用来取交易日窗口 */
      } finally {
        this.dateNavLoading = false
      }
      await this.loadMonitoring()
      await this.loadAxisDates()
    },
    async loadAxisDates() {
      if (!this.tradeDate) return
      this.dateNavLoading = true
      try {
        const entries = (this.monitorItems || [])
          .map((item) => String(item.entryDate || item.firstFourBoardDate || item.fourBoardDate || '').slice(0, 10))
          .filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item))
          .sort()
        const entryStart = this.addCalendarDays(entries[0] || this.tradeDate, -30)
        const windowStart = this.addCalendarDays(this.tradeDate, -80)
        const startDate = entryStart < windowStart ? entryStart : windowStart
        const res = await stockApi.fetchReviewSeries({ startDate, endDate: this.tradeDate })
        const dates = (res && res.tradeDates ? res.tradeDates : [])
          .map((item) => String(item).slice(0, 10))
          .filter((item) => item && item >= startDate && item <= this.tradeDate)
        this.calendarDates = dates
        const byDate = {}
        const dayMarks = {}
        const queue = dates.slice()
        const workers = Array.from({ length: 6 }, async () => {
          while (queue.length) {
            const date = queue.shift()
            try {
              const row = await stockApi.fetchDailyData(date)
              const map = {}
              for (const item of (row && row.limitUpList) || []) {
                const code = String(item.code || '').split('.')[0]
                if (!code) continue
                map[code] = {
                  boards: this.boardsOfLimitUp(item),
                  name: item.name || code,
                  time: String(item.limitUpTime || '')
                }
              }
              byDate[date] = map
              const score = row && row.greedyNodeScore != null ? Number(row.greedyNodeScore) : null
              dayMarks[String(row && row.tradeDate || date).slice(0, 10)] = {
                nodeScore: Number.isFinite(score) ? score : null,
                nodeHit: Number.isFinite(score) && score >= 7
              }
            } catch (err) {
              byDate[date] = {}
            }
          }
        })
        await Promise.all(workers)
        this.limitUpByDate = byDate
        this.axisDates = dates
        await Promise.all([this.loadCloses(dates, this.heightLeaderCodes(byDate, dates)), this.loadSoilMarks(dayMarks)])
        this.dayMarks = dayMarks
        this.loadVolumeMarks()
      } catch (err) {
        this.axisDates = []
        this.calendarDates = []
        this.closesByCode = {}
        this.$message.error((err && err.message) || '交易日加载失败')
      } finally {
        this.dateNavLoading = false
        if (this.pathMode === 'peak') this.loadBurstTop()
        if (this.isTrial) this.loadFinalBoard()
      }
    },
    async loadFinalBoard() {
      if (!this.isTrial || !this.tradeDate) return
      const stamp = this.tradeDate
      this.finalsLoading = true
      try {
        const data = await stockApi.getReviewBoard(stamp, 60)
        if (this.tradeDate !== stamp) return
        const columns = (data && data.columns) || []
        const map = {}
        columns.forEach((col) => {
          map[col.date] = (col.stocks || []).map((stock) => ({
            code: stock.code,
            name: stock.name || stock.code,
            score: this.midScore(stock),
            cap: this.capYi(stock),
            star: this.starBoards(stock)
          }))
        })
        this.finalsByDate = map
      } catch (err) {
        if (this.tradeDate === stamp) {
          this.finalsByDate = {}
          this.$message.error((err && err.message) || '首板终局加载失败')
        }
      } finally {
        if (this.tradeDate === stamp) this.finalsLoading = false
      }
    },
    midScore(stock) {
      const n = Number(stock && stock.midChip)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}分`
    },
    capYi(stock) {
      const n = Number(stock && stock.freeFloatMarketCap)
      if (!Number.isFinite(n)) return '—'
      return `${n.toFixed(1)}亿`
    },
    starBoards(stock) {
      const n = Number(stock && stock.finalBoards)
      if (!Number.isFinite(n)) return '—'
      return String(Math.round(n))
    },
    async loadVolumeMarks() {
      const pool = new Set((this.monitorItems || []).map((item) => String(item.code || '').split('.')[0]))
      const leaders = this.boardLeaderStocks || []
      const extras = this.visibleStocks || []
      const byCode = new Map()
      leaders.forEach((stock) => {
        const days = (stock.boardDays && stock.boardDays.length) ? stock.boardDays : []
        if (days.length) byCode.set(stock.code, { code: stock.code, name: stock.name, days, inPool: pool.has(stock.code) })
      })
      const calendar = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      const addPoolDays = (stock) => {
        if (!stock || !stock.code) return
        const code = String(stock.code).split('.')[0]
        const days = []
        calendar.forEach((date) => {
          const hit = (this.limitUpByDate[date] || {})[code]
          if (hit && hit.boards) days.push({ date, boards: hit.boards })
        })
        if (byCode.has(code)) {
          const row = byCode.get(code)
          row.inPool = row.inPool || pool.has(code)
          if (pool.has(code) && days.length) {
            const seen = {}
            ;(row.days || []).forEach((day) => { seen[day.date] = day })
            days.forEach((day) => { seen[day.date] = day })
            row.days = Object.keys(seen).sort().map((date) => seen[date])
          }
          return
        }
        if (days.length) byCode.set(code, { code, name: stock.name, days, inPool: pool.has(code) })
      }
      extras.forEach(addPoolDays)
      ;(this.monitorItems || []).forEach((item) => addPoolDays({ code: String(item.code || '').split('.')[0], name: item.name }))
      const stocks = Array.from(byCode.values())
      const stamp = `${this.tradeDate}|${stocks.map((stock) => stock.code).sort().join(',')}`
      if (!stocks.length || !this.tradeDate) {
        this.volumeMarksByCode = {}
        this.volumeStamp = stamp
        return
      }
      if (this.volumeStamp === stamp) return
      this.volumeStamp = stamp
      try {
        const data = await stockApi.fetchV2AVolumeMarks({ stocks, through: this.tradeDate })
        if (this.volumeStamp !== stamp) return
        const map = {}
        for (const mark of (data && data.marks) || []) {
          const code = String(mark.code || '').split('.')[0]
          const date = String(mark.date || '').slice(0, 10)
          const boards = Math.floor(Number(mark.boards))
          if (!code || !date || !boards) continue
          if (!map[code]) map[code] = []
          map[code].push({ date, boards, kind: mark.kind === 'high' ? 'high' : 'v2a' })
        }
        this.volumeMarksByCode = map
      } catch (err) {
        if (this.volumeStamp === stamp) this.volumeMarksByCode = {}
      }
    },
    async loadCloses(dates, extraCodes) {
      const codes = [...new Set([
        ...(this.monitorItems || []).map((item) => String(item.code || '').split('.')[0]),
        ...(extraCodes || [])
      ].filter((item) => /^\d{6}$/.test(item)))]
      if (!codes.length || !dates.length) {
        this.closesByCode = {}
        this.opensByCode = {}
        return
      }
      try {
        const res = await stockApi.fetchStockDailyCloses({
          codes: codes.slice(0, 80),
          startDate: this.addCalendarDays(dates[0], -45),
          endDate: dates[dates.length - 1]
        })
        const closes = {}
        const opens = {}
        for (const row of (res && res.rows) || []) {
          if (!closes[row.code]) closes[row.code] = {}
          if (!opens[row.code]) opens[row.code] = {}
          closes[row.code][row.tradeDate] = row.close
          if (Number.isFinite(Number(row.open))) opens[row.code][row.tradeDate] = Number(row.open)
        }
        this.closesByCode = closes
        this.opensByCode = opens
      } catch (err) {
        this.closesByCode = {}
        this.opensByCode = {}
        this.$message.error((err && err.message) || '每日收盘加载失败')
      }
    },
    async loadSoilMarks(dayMarks) {
      if (!this.tradeDate) return
      try {
        const data = await stockApi.getRelayEcologyCurve(this.tradeDate, 60)
        for (const point of (data && data.series) || []) {
          const date = String(point.date || '').slice(0, 10)
          if (!date) continue
          if (!dayMarks[date]) dayMarks[date] = { nodeScore: null, nodeHit: false }
          dayMarks[date].soil = point.soilState === 'S_PLUS' || point.soilState === 'S' ? point.soilState : 'NORMAL'
        }
      } catch (err) {
        /* 土壤没取到时，日期下显示无 */
      }
    },
    async goPrevTradeDay() {
      if (!this.tradeDate || this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.prevHistoryFirstBoardTradeDate(this.tradeDate)
        if (!res || !res.ok || !res.prevTradeDate) {
          this.$message.warning((res && res.message) || '无上一交易日')
          return
        }
        this.tradeDate = res.prevTradeDate
        await this.loadMonitoring()
        await this.loadAxisDates()
      } catch (err) {
        this.$message.error((err && err.message) || '获取上一交易日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    async goNextTradeDay() {
      if (!this.tradeDate || this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.nextHistoryFirstBoardTradeDate(this.tradeDate)
        if (!res || !res.ok || !res.nextTradeDate) {
          this.$message.warning((res && res.message) || '无下一交易日')
          return
        }
        this.tradeDate = res.nextTradeDate
        await this.loadMonitoring()
        await this.loadAxisDates()
      } catch (err) {
        this.$message.error((err && err.message) || '获取下一交易日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    async goTodayTradeDay() {
      if (this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.resolveHistoryFirstBoardTradeDate(null)
        if (!res || !res.ok || !res.tradeDate) {
          this.$message.warning((res && res.message) || '无法解析今日交易日')
          return
        }
        this.tradeDate = res.tradeDate
        await this.loadMonitoring()
        await this.loadAxisDates()
      } catch (err) {
        this.$message.error((err && err.message) || '跳转今日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    signed(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`
    },
    displayReturn(stock) {
      const points = stock.points || []
      const end = Math.min(this.visibleLastIndex(), points.length - 1)
      for (let i = end; i >= 0; i -= 1) {
        const value = applyScenario(points[i] && points[i].cumulativeReturn, this.sourceId)
        if (value != null && Number.isFinite(Number(value))) return Number(value)
      }
      return null
    },
    displayCurrentBoard(stock) {
      const point = stock.points[Math.min(this.visibleLastIndex(), stock.points.length - 1)]
      return point.dailyBoardHeight
    },
    displayMaxBoard(stock) {
      return stock.points.slice(0, this.visibleLastIndex() + 1).reduce((max, point) => Math.max(max, point.dailyBoardHeight), 0)
    },
    stageTag(stage) {
      if (stage === '衰老阶段') return 'danger'
      if (stage === '停滞阶段') return 'warning'
      if (stage === '扩张阶段') return 'success'
      return ''
    },
    statusTag(status) {
      if (status === '转弱' || status === '衰老观察') return 'danger'
      if (status === '停滞') return 'warning'
      if (status === '扩张' || status === '活跃') return 'success'
      return 'info'
    },
    stageKey(stage) {
      if (stage === '兴起') return 'rise'
      if (stage === '扩张') return 'expand'
      if (stage === '停滞') return 'stall'
      return 'age'
    },
    selectStock(code) {
      this.activeCode = String(code || '').split('.')[0]
      this.detailTab = 'overview'
      this.syncConceptDraft()
    },
    syncConceptDraft() {
      const code = String(this.activeCode || '').split('.')[0]
      const concepts = this.conceptNames(this.savedConcepts && this.savedConcepts[code])
      const nameKeys = this.conceptNames(this.savedNameKeys && this.savedNameKeys[code])
      this.conceptDraft = this.conceptNames(concepts.concat(nameKeys))
      this.conceptMatch = this.conceptMatchMode(code)
    },
    async loadSavedConcepts() {
      try {
        const data = await stockApi.getCycleLineConcepts()
        this.savedConcepts = (data && data.byCode) || {}
        this.savedNameKeys = (data && data.nameByCode) || {}
        this.savedKingdomTitles = (data && data.kingdomTitleByCode) || {}
        this.savedMatchByCode = (data && data.matchByCode) || {}
        this.kingdomHidden = (data && data.hiddenByCode) || {}
        this.kingdomMembers = (data && data.membersByCode) || {}
        this.dropDeletedKingdoms()
        this.syncConceptDraft()
        this.loadKingdomGallery()
      } catch (err) {
        this.savedConcepts = {}
        this.savedNameKeys = {}
        this.savedKingdomTitles = {}
        this.savedMatchByCode = {}
        this.kingdomHidden = {}
        this.kingdomMembers = {}
      }
    },
    async ensureConceptNames(codes) {
      const pending = this._conceptPending || (this._conceptPending = {})
      const missing = (codes || []).filter((code) => code && !Object.prototype.hasOwnProperty.call(this.conceptsByCode, code) && !pending[code])
      if (!missing.length) return
      missing.forEach((code) => { pending[code] = true })
      try {
        const data = await stockApi.fetchConceptNames({ codes: missing })
        const names = (data && data.names) || {}
        const next = { ...this.conceptsByCode }
        missing.forEach((code) => { next[code] = names[code] || [] })
        this.conceptsByCode = next
      } catch (err) {
        missing.forEach((code) => { delete pending[code] })
      }
    },
    saveKingdom() {
      if (!this.conceptNames(this.conceptDraft).length) {
        this.$message.warning('先选择或输入概念')
        return
      }
      const code = String(this.activeCode || '').split('.')[0]
      this.kingdomSaveName = this.kingdomShortName(this.stockNameOf(code) || (this.activeStock && this.activeStock.name) || '')
      this.kingdomSaveKind = 'total'
      this.kingdomTotalCode = ''
      this.kingdomBranchCode = ''
      this.kingdomSaveOpen = true
    },
    async confirmSaveKingdom() {
      const title = String(this.kingdomSaveName || '').trim()
      if (!title) {
        this.$message.warning('请填写名称')
        return
      }
      const king = String(this.kingdomTotalCode || '').split('.')[0]
      const branch = String(this.kingdomBranchCode || '').split('.')[0]
      if (!/^\d{6}$/.test(king)) {
        this.$message.warning('请选择总龙')
        return
      }
      if (!/^\d{6}$/.test(branch) || !this.kingdomBranchRows.some((row) => row.code === branch)) {
        this.$message.warning('请选择分支龙')
        return
      }
      const rows = this.kingdomBranchRows.slice()
      if (!rows.some((row) => row.code === king)) {
        const total = this.kingdomTotalRows.find((row) => row.code === king)
        if (total) rows.unshift(total)
      }
      const tradeDate = String(this.tradeDate || '')
      if (!/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) {
        this.$message.warning('先选择交易日')
        return
      }
      const active = String(this.activeCode || '').split('.')[0]
      const split = this.splitConceptDraft(active, this.conceptDraft)
      const match = this.conceptMatch === 'and' ? 'and' : 'or'
      const peers = rows.filter((row) => row.code !== king).map((row) => ({ code: row.code, name: row.name }))
      peers.sort((a, b) => {
        if (a.code === branch) return -1
        if (b.code === branch) return 1
        return 0
      })
      const prev = ((this.kingdomMembers && this.kingdomMembers[king]) || []).map((item) => String((item && item.code) || '').split('.')[0])
      const keep = {}
      peers.forEach((row) => { keep[row.code] = true })
      const removed = prev.filter((code) => /^\d{6}$/.test(code) && code !== king && !keep[code])
      this.conceptSaving = true
      try {
        await stockApi.saveCycleLineConcept({
          code: king,
          concepts: split.concepts,
          nameKeys: split.nameKeys,
          match,
          kingdomTitle: title
        })
        await stockApi.saveCycleLineMembers({ code: king, tradeDate, members: peers, removed })
        this.forgetDeletedKingdom(king)
        this.kingdomSaveOpen = false
        await this.loadSavedConcepts()
        this.$message.success('王国已保存')
      } catch (err) {
        this.$message.error((err && err.message) || '王国保存失败')
      } finally {
        this.conceptSaving = false
      }
    },
    async deleteKingdom(code) {
      const king = String(code || '').split('.')[0]
      if (!/^\d{6}$/.test(king)) return
      const title = (this.savedKingdomTitles && this.savedKingdomTitles[king]) || this.kingdomLabel(king) || king
      try {
        await this.$confirm(`删除王国「${title}」？里面的股票会回到观察池。`, '删除王国', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (err) {
        return
      }
      const members = (this.kingdomMembers && this.kingdomMembers[king]) || []
      const removed = []
      for (let i = 0; i < members.length; i += 1) {
        const peer = String((members[i] && members[i].code) || '').split('.')[0]
        if (/^\d{6}$/.test(peer) && peer !== king) removed.push(peer)
      }
      try {
        await stockApi.saveCycleLineConcept({ code: king, concepts: [], nameKeys: [], kingdomTitle: '' })
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(this.tradeDate || ''))) {
          await stockApi.saveCycleLineMembers({ code: king, tradeDate: this.tradeDate, members: [], removed })
        }
        await stockApi.saveCycleLineHidden({ code: king, hidden: [] })
        const tabs = this.readKingdomTabs().filter((item) => {
          if (!item) return false
          if (item.name === title) return false
          const list = item.members || []
          for (let i = 0; i < list.length; i += 1) {
            const peer = String((list[i] && list[i].code) || '').split('.')[0]
            if (peer === king && list[i].role === 'leader') return false
          }
          return true
        })
        persistKingdomTabs(tabs)
        this.rememberDeletedKingdom(king)
        this.dropDeletedKingdoms()
        if (this.kingdomFocusCode === king) this.showCycleChart()
        await this.loadKingdomGallery()
        this.$message.success('王国已删除，股票回到观察池')
      } catch (err) {
        this.$message.error((err && err.message) || '删除王国失败')
      }
    },
    forgetDeletedKingdom(code) {
      const key = 'cycle.deletedKingdoms'
      let list = []
      try {
        const raw = JSON.parse(sessionStorage.getItem(key) || '[]')
        if (Array.isArray(raw)) list = raw
      } catch (err) {
        list = []
      }
      const next = list.filter((item) => item !== code)
      try {
        sessionStorage.setItem(key, JSON.stringify(next))
      } catch (err) {
        /* ignore */
      }
    },
    rememberDeletedKingdom(code) {
      const key = 'cycle.deletedKingdoms'
      let list = []
      try {
        const raw = JSON.parse(sessionStorage.getItem(key) || '[]')
        if (Array.isArray(raw)) list = raw
      } catch (err) {
        list = []
      }
      if (list.indexOf(code) < 0) list.push(code)
      try {
        sessionStorage.setItem(key, JSON.stringify(list))
      } catch (err) {
        /* 记不住时这一页仍会先拿掉缩略图 */
      }
    },
    dropDeletedKingdoms() {
      let list = []
      try {
        const raw = JSON.parse(sessionStorage.getItem('cycle.deletedKingdoms') || '[]')
        if (Array.isArray(raw)) list = raw
      } catch (err) {
        list = []
      }
      if (!list.length) return
      const titles = Object.assign({}, this.savedKingdomTitles || {})
      const book = Object.assign({}, this.kingdomMembers || {})
      const hidden = Object.assign({}, this.kingdomHidden || {})
      for (let i = 0; i < list.length; i += 1) {
        delete titles[list[i]]
        delete book[list[i]]
        delete hidden[list[i]]
      }
      this.savedKingdomTitles = titles
      this.kingdomMembers = book
      this.kingdomHidden = hidden
      this.kingdomCards = (this.kingdomCards || []).filter((card) => list.indexOf(card.code) < 0)
    },
    async saveConceptAndPick() {
      const ok = await this.saveMainConcept()
      if (!ok) return
      const mode = this.conceptMatch === 'and' ? 'and' : 'or'
      const code = String(this.activeCode || '').split('.')[0]
      const tokens = this.savedTokens(code)
      const rows = this.poolPickRows || []
      const pending = []
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && rows[i].code) pending.push(rows[i].code)
      }
      await this.ensureConceptNames(pending)
      const codes = []
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i]
        if (!row || !row.code) continue
        if (this.stockMatchesSaved(row, tokens.concepts, tokens.nameKeys, mode)) codes.push(row.code)
      }
      this.poolPicked = codes
    },
    async saveMainConcept(doneText, kingdomTitle) {
      const code = String(this.activeCode || '').split('.')[0]
      if (!/^\d{6}$/.test(code) || this.conceptSaving) return false
      this.conceptSaving = true
      try {
        const split = this.splitConceptDraft(code, this.conceptDraft)
        const match = this.conceptMatch === 'and' ? 'and' : 'or'
        const keepTitle = typeof kingdomTitle === 'string'
        const body = {
          code,
          concepts: split.concepts,
          nameKeys: split.nameKeys,
          match
        }
        if (keepTitle) body.kingdomTitle = kingdomTitle
        const data = await stockApi.saveCycleLineConcept(body)
        this.savedConcepts = (data && data.byCode) || {}
        this.savedNameKeys = (data && data.nameByCode) || {}
        this.savedMatchByCode = (data && data.matchByCode) || {}
        if (keepTitle) {
          if (kingdomTitle) this.forgetDeletedKingdom(code)
          this.savedKingdomTitles = (data && data.kingdomTitleByCode) || this.savedKingdomTitles
          this.dropDeletedKingdoms()
          this.loadKingdomGallery()
        }
        this.syncConceptDraft()
        const saved = split.concepts.length || split.nameKeys.length
        const text = typeof doneText === 'string' ? doneText : ''
        this.$message.success(text || (saved ? '主概念已保存' : '主概念已清除'))
        return true
      } catch (err) {
        this.$message.error((err && err.message) || '主概念保存失败')
        return false
      } finally {
        this.conceptSaving = false
      }
    },
    openKingdom() {
      this.kingdomFocusCode = ''
      this.kingdomFocusStock = null
      this.kingdomOpen = true
      this.kingdomPeers = []
      this.kingdomExtra = this.savedKingdomMembers()
      this.kingdomCatchups = []
      this.kingdomNote = ''
      this.refreshKingdomPeers()
    },
    showCycleChart() {
      this.kingdomOpen = false
      this.kingdomFocusCode = ''
      this.kingdomFocusStock = null
    },
    async focusSavedKingdom(code) {
      const short = String(code || '').split('.')[0]
      if (!/^\d{6}$/.test(short)) return
      const dates = this.kingdomSourceDates || []
      this.kingdomOpen = true
      this.kingdomFocusCode = short
      this.kingdomLoading = true
      await this.ensurePeerCloses([short], dates)
      const name = this.stockNameOf(short) || short
      const wavePoints = this.buildWavePoints(short, (this.startByCode && this.startByCode[short]) || '', dates)
      this.kingdomFocusStock = {
        code: short,
        name,
        color: '#f25c54',
        wavePoints,
        points: wavePoints
      }
      this.kingdomPeers = []
      this.kingdomExtra = this.savedKingdomMembers()
      this.kingdomCatchups = []
      this.kingdomNote = ''
      await this.refreshKingdomPeers()
      this.$nextTick(() => this.redrawKingdom())
    },
    galleryDates() {
      return (this.kingdomSourceDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
    },
    stockNameOf(code) {
      const short = String(code || '').split('.')[0]
      const pools = []
      const monitors = this.monitorItems || []
      const visible = this.visibleStocks || []
      monitors.forEach((item) => pools.push(item))
      visible.forEach((item) => pools.push(item))
      const book = this.kingdomMembers || {}
      Object.keys(book).forEach((king) => {
        const members = book[king] || []
        members.forEach((item) => pools.push(item))
      })
      const found = pools.find((item) => String((item && item.code) || '').split('.')[0] === short && item.name && item.name !== short)
      return found ? found.name : ''
    },
    waveValues(code, dates) {
      const first = (this.startByCode && this.startByCode[code]) || ''
      return this.buildWavePoints(code, first, dates).map((point) => {
        const n = Number(point && point.cumulativeReturn)
        return point && point.cumulativeReturn != null && Number.isFinite(n) ? n : null
      })
    },
    async lookupKingNames(codes) {
      const found = {}
      for (let i = 0; i < codes.length; i += 1) {
        const code = codes[i]
        try {
          const res = await stockApi.searchStocks(code)
          const rows = (res && res.data) || []
          const row = rows.find((item) => String((item && item.code) || '').split('.')[0] === code)
          if (row && row.name) found[code] = row.name
        } catch (err) {
          found[code] = ''
        }
      }
      return found
    },
    kingdomShortName(name) {
      return String(name || '').replace(/(股份有限公司|股份|集团|有限公司|有限)$/, '')
    },
    kingdomLabel(code) {
      const title = this.savedKingdomTitles && this.savedKingdomTitles[code]
      return title || this.stockNameOf(code) || code
    },
    monitorPoolSet() {
      const pool = {}
      ;(this.monitorItems || []).forEach((item) => {
        const code = String((item && item.code) || '').split('.')[0]
        if (/^\d{6}$/.test(code)) pool[code] = true
      })
      return pool
    },
    dropKingdomIfKingLeft() {
      const code = this.kingdomFocusCode
      if (!code || !this.kingdomOpen) return
      if (this.monitorPoolSet()[code]) return
      this.showCycleChart()
      this.$message.info('国王已不在监控池，王国先退出')
    },
    savedKingdomCodes() {
      const pool = this.monitorPoolSet()
      const titles = this.savedKingdomTitles || {}
      return Object.keys(titles).filter((code) => /^\d{6}$/.test(code) && titles[code] && pool[code])
    },
    async loadKingdomGallery() {
      const book = this.kingdomMembers || {}
      const kings = this.savedKingdomCodes()
      const dates = this.galleryDates()
      if (!kings.length) {
        this.kingdomCards = []
        return
      }
      const codes = []
      kings.forEach((code) => {
        codes.push(code)
        const members = book[code] || []
        members.forEach((item) => codes.push(String((item && item.code) || '').split('.')[0]))
      })
      this.kingdomGalleryLoading = true
      try {
        await this.ensurePeerCloses([...new Set(codes.filter((code) => /^\d{6}$/.test(code)))], dates)
        const missing = kings.filter((code) => !this.stockNameOf(code))
        const names = await this.lookupKingNames(missing)
        this.kingdomCards = kings.map((code) => {
          const kingName = this.stockNameOf(code) || names[code] || code
          const series = [{
            name: kingName,
            color: '#f25c54',
            values: this.waveValues(code, dates)
          }]
          const members = book[code] || []
          members.forEach((item, index) => {
            const peer = String((item && item.code) || '').split('.')[0]
            if (!/^\d{6}$/.test(peer)) return
            series.push({
              name: (item && item.name) || this.stockNameOf(peer) || peer,
              color: this.peerColor(index + 1),
              values: this.waveValues(peer, dates)
            })
          })
          return {
            code,
            title: this.kingdomLabel(code),
            dates,
            series
          }
        })
      } finally {
        this.kingdomGalleryLoading = false
        this.$nextTick(() => this.redrawKingdom())
      }
    },
    redrawKingdom() {
      const panel = this.$refs.kingdomPanel
      if (panel && panel.draw) panel.draw()
    },
    async refreshKingdomPeers() {
      const names = this.kingdomConceptList
      const dates = this.kingdomDates
      if (!this.kingdomOpen) return
      if (!dates.length) {
        this.kingdomPeers = []
        this.kingdomCatchups = []
        this.kingdomLoading = false
        this.kingdomNote = ''
        return
      }
      if (!names.length) {
        this.kingdomPeers = []
        this.kingdomLoading = false
        this.kingdomNote = '先选定主概念'
        await this.applyKingdomExtras(true)
        this.kingdomCatchups = await this.loadPeerCatchupMarks(this.kingdomPeers, dates)
        return
      }
      this.kingdomLoading = true
      this.kingdomNote = ''
      const seen = {}
      const codes = []
      dates.forEach((date) => {
        this.limitUpItems(date).forEach((item) => {
          if (seen[item.code]) return
          seen[item.code] = true
          codes.push(item.code)
        })
      })
      const chunk = 400
      for (let i = 0; i < codes.length; i += chunk) {
        await this.ensureConceptNames(codes.slice(i, i + chunk))
      }
      if (!this.kingdomOpen) return
      const peers = this.collectKingdomPeers(dates, names)
      await this.ensurePeerCloses(peers.map((peer) => peer.code), dates)
      if (!this.kingdomOpen) return
      const afterDate = this.kingFourBoardDate()
      this.kingdomPeers = peers
        .map((peer) => {
          const path = this.peerStreakReturns(peer.code, dates, afterDate)
          return { ...peer, returns: path.values, tails: path.tails, premiums: path.premiums || [] }
        })
        .filter((peer) => (peer.returns || []).some((value) => Number.isFinite(value)))
      this.kingdomLoading = false
      this.kingdomNote = this.kingdomPeers.length ? '' : (afterDate ? '同概念暂无3连板' : '国王未到4板')
      await this.applyKingdomExtras(true)
      if (this.kingdomPeers.length) this.kingdomNote = ''
      this.kingdomCatchups = await this.loadPeerCatchupMarks(this.kingdomPeers, dates)
    },
    peerColor(index) {
      const palette = ['#f25c54', '#3b82f6', '#ec4899', '#8b5cf6', '#22c55e', '#e6a23c', '#14b8a6', '#6366f1', '#f97316', '#84cc16', '#06b6d4', '#a855f7', '#0ea5e9', '#d946ef', '#65a30d']
      return palette[index % palette.length]
    },
    async addKingdomPeers(rows, quiet) {
      const kingCode = this.kingdomKing ? this.kingdomKing.code : ''
      const have = {}
      ;(this.kingdomExtra || []).forEach((stock) => { have[stock.code] = true })
      ;(this.kingdomPeers || []).forEach((peer) => { have[peer.code] = true })
      const incoming = []
      for (let i = 0; i < (rows || []).length; i += 1) {
        const row = rows[i] || {}
        const code = String(row.code || '').split('.')[0]
        const name = String(row.name || '').trim() || code
        if (!/^\d{6}$/.test(code) || code === kingCode || have[code]) continue
        have[code] = true
        incoming.push({ code, name })
      }
      if (!incoming.length) {
        if (!quiet) this.$message.warning('这些股票已经在图上')
        return { added: [], skipped: [] }
      }
      this.kingdomExtra = (this.kingdomExtra || []).concat(incoming)
      const drawn = await this.applyKingdomExtras(quiet)
      this.kingdomCatchups = await this.loadPeerCatchupMarks(this.kingdomPeers, this.kingdomDates)
      return drawn || { added: [], skipped: [] }
    },
    async applyKingdomExtras(quiet) {
      const dates = this.kingdomDates
      const kingCode = this.kingdomKing ? this.kingdomKing.code : ''
      const hidden = this.hiddenCodeSet(kingCode)
      const have = {}
      ;(this.kingdomPeers || []).forEach((peer) => { have[peer.code] = true })
      const pending = []
      for (let i = 0; i < (this.kingdomExtra || []).length; i += 1) {
        const stock = this.kingdomExtra[i]
        if (!stock || !stock.code || stock.code === kingCode || hidden[stock.code] || have[stock.code]) continue
        have[stock.code] = true
        pending.push(stock)
      }
      if (!pending.length || !dates.length) return
      await this.ensurePeerCloses(pending.map((stock) => stock.code), dates)
      const start = (this.kingdomPeers || []).length
      const added = []
      const skipped = []
      const skippedCodes = {}
      for (let i = 0; i < pending.length; i += 1) {
        const stock = pending[i]
        let path = this.peerStreakReturns(stock.code, dates, '', true)
        let finite = false
        for (let j = 0; j < (path.values || []).length; j += 1) {
          if (Number.isFinite(path.values[j])) finite = true
        }
        if (!finite) path = this.manualWaveReturns(stock.code, dates)
        finite = false
        for (let j = 0; j < (path.values || []).length; j += 1) {
          if (Number.isFinite(path.values[j])) finite = true
        }
        if (!finite) {
          skipped.push(stock.name || stock.code)
          skippedCodes[stock.code] = true
          continue
        }
        const boards = dates.map((date) => {
          const hit = (this.limitUpByDate[date] || {})[stock.code]
          const boardsN = hit && Number(hit.boards)
          return boardsN > 0 ? boardsN : 0
        })
        added.push({
          code: stock.code,
          name: stock.name || stock.code,
          boards,
          color: this.peerColor(start + added.length),
          returns: path.values,
          tails: path.tails,
          premiums: path.premiums || []
        })
      }
      if (added.length) this.kingdomPeers = (this.kingdomPeers || []).concat(added)
      if (skipped.length && !quiet) {
        this.kingdomExtra = (this.kingdomExtra || []).filter((stock) => !skippedCodes[stock.code])
      }
      const drawn = {
        added: added.map((peer) => peer.name || peer.code),
        skipped
      }
      if (quiet) return drawn
      if (added.length && skipped.length) this.$message.warning(`已加入 ${added.length} 只。${skipped.join('、')} 没有收盘，没能画出来`)
      else if (skipped.length) this.$message.warning(`${skipped.join('、')} 没有收盘，没能画出来`)
      else if (added.length) this.$message.success(`已加入 ${added.length} 只`)
      return drawn
    },
    async ensurePeerCloses(codes, dates) {
      const missing = (codes || []).filter((code) => {
        const book = this.closesByCode && this.closesByCode[code]
        const opened = this.opensByCode && this.opensByCode[code]
        return !book || !Object.keys(book).length || !opened || !Object.keys(opened).length
      })
      if (!missing.length || !dates.length) return
      const startDate = this.addCalendarDays(dates[0], -90)
      const endDate = dates[dates.length - 1]
      const closes = { ...(this.closesByCode || {}) }
      const opens = { ...(this.opensByCode || {}) }
      const chunk = 80
      for (let i = 0; i < missing.length; i += chunk) {
        const res = await stockApi.fetchStockDailyCloses({
          codes: missing.slice(i, i + chunk),
          startDate,
          endDate
        })
        for (const row of (res && res.rows) || []) {
          const code = String(row.code || '').split('.')[0]
          if (!code) continue
          if (!closes[code]) closes[code] = {}
          if (!opens[code]) opens[code] = {}
          closes[code][row.tradeDate] = row.close
          if (Number.isFinite(Number(row.open))) opens[code][row.tradeDate] = Number(row.open)
        }
      }
      this.closesByCode = closes
      this.opensByCode = opens
    },
    async loadPeerCatchupMarks(peers, dates) {
      const list = peers || []
      if (!list.length || !this.tradeDate) return []
      const calendar = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      const stocks = []
      list.forEach((peer) => {
        const days = []
        calendar.forEach((date) => {
          const hit = (this.limitUpByDate[date] || {})[peer.code]
          if (hit && hit.boards) days.push({ date, boards: hit.boards })
        })
        if (days.length) stocks.push({ code: peer.code, name: peer.name, days, inPool: true })
      })
      if (!stocks.length) return []
      const marks = []
      const seen = {}
      const chunk = 80
      try {
        for (let i = 0; i < stocks.length; i += chunk) {
          const data = await stockApi.fetchV2AVolumeMarks({
            stocks: stocks.slice(i, i + chunk),
            through: this.tradeDate
          })
          if (!this.kingdomOpen) return []
          for (const mark of (data && data.marks) || []) {
            const code = String(mark.code || '').split('.')[0]
            const date = String(mark.date || '').slice(0, 10)
            const boards = Math.floor(Number(mark.boards))
            if (!code || !date || mark.kind !== 'high' || boards < 5) continue
            if (dates.indexOf(date) < 0) continue
            const key = `${code}|${date}`
            if (seen[key]) continue
            seen[key] = true
            const peer = list.find((item) => item.code === code)
            const index = dates.indexOf(date)
            const ret = peer && peer.returns ? peer.returns[index] : null
            const tail = peer && peer.tails ? peer.tails[index] : null
            const y = Number.isFinite(ret) ? ret : (Number.isFinite(tail) ? tail : null)
            marks.push({ code, date, boards, y, name: peer ? peer.name : code })
          }
        }
      } catch (err) {
        return marks
      }
      return marks
    },
    hiddenCodeSet(code) {
      const list = (this.kingdomHidden && this.kingdomHidden[String(code || '').split('.')[0]]) || []
      const set = {}
      list.forEach((item) => {
        const short = String(item || '').split('.')[0]
        if (short) set[short] = true
      })
      return set
    },
    savedKingdomMembers() {
      const king = this.kingdomKing ? this.kingdomKing.code : ''
      const asOf = String(this.tradeDate || '')
      const list = (this.kingdomMembers && this.kingdomMembers[king]) || []
      const rows = []
      const seen = {}
      list.forEach((item) => {
        const code = String((item && item.code) || '').split('.')[0]
        const savedDate = String((item && item.savedDate) || '')
        if (!/^\d{6}$/.test(code) || code === king || seen[code]) return
        if (savedDate && asOf && savedDate > asOf) return
        seen[code] = true
        rows.push({ code, name: (item && item.name) || code })
      })
      return rows
    },
    async saveKingdomRoster(payload) {
      const code = this.kingdomKing ? this.kingdomKing.code : ''
      const tradeDate = String(this.tradeDate || '')
      if (!/^\d{6}$/.test(code) || !/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) return
      const body = payload && payload.kept ? payload : { kept: [], removed: payload || [] }
      const kept = body.kept || []
      const removed = body.removed || []
      const picked = body.picked || []
      this.kingdomSaving = true
      try {
        const data = await stockApi.saveCycleLineMembers({ code, tradeDate, members: kept, removed })
        this.kingdomMembers = (data && data.membersByCode) || {}
        this.kingdomHidden = (data && data.hiddenByCode) || {}
        const gone = {}
        removed.forEach((item) => { gone[String(item || '').split('.')[0]] = true })
        this.kingdomExtra = (this.kingdomExtra || []).filter((stock) => !gone[stock.code])
        this.kingdomPeers = (this.kingdomPeers || []).filter((peer) => !gone[peer.code])
        this.kingdomCatchups = (this.kingdomCatchups || []).filter((item) => !gone[item.code])
        const drawn = picked.length ? await this.addKingdomPeers(picked, true) : { added: [], skipped: [] }
        const have = {}
        ;(this.kingdomExtra || []).forEach((stock) => { have[stock.code] = true })
        ;(this.kingdomPeers || []).forEach((peer) => { have[peer.code] = true })
        this.savedKingdomMembers().forEach((stock) => {
          if (!gone[stock.code] && !have[stock.code]) this.kingdomExtra.push(stock)
        })
        await this.applyKingdomExtras(true)
        const addedNames = (drawn && drawn.added) || []
        const skippedNames = (drawn && drawn.skipped) || []
        if (skippedNames.length) this.$message.warning(`${skippedNames.join('、')} 没有收盘，没能画出来`)
        else if (addedNames.length) this.$message.success(`已保存，${addedNames.join('、')} 已画上图`)
        else this.$message.success(removed.length ? '已保存，去掉的不会再出现' : '已按今天保存，之后的日期也能看到')
        this.loadKingdomGallery()
      } catch (err) {
        this.$message.error((err && err.message) || '保存失败')
      } finally {
        this.kingdomSaving = false
      }
    },
    kingFourBoardDate() {
      const code = this.kingdomKing ? this.kingdomKing.code : ''
      if (!code) return ''
      const calendar = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      for (let i = 0; i < calendar.length; i += 1) {
        const item = (this.limitUpByDate[calendar[i]] || {})[code]
        if (item && Number(item.boards) >= 4) return calendar[i]
      }
      return ''
    },
    manualWaveReturns(code, dates) {
      const points = this.buildWavePoints(code, dates[0], dates)
      const values = points.map((point) => {
        const n = Number(point && point.cumulativeReturn)
        return point && point.cumulativeReturn != null && Number.isFinite(n) ? n : null
      })
      return { values, tails: dates.map(() => null) }
    },
    peerStreakReturns(code, dates, afterDate, free) {
      const closes = (this.closesByCode && this.closesByCode[code]) || {}
      const opens = (this.opensByCode && this.opensByCode[code]) || {}
      const calendar = (this.calendarDates || []).filter((date) => !this.tradeDate || date <= this.tradeDate)
      const streaks = []
      let run = []
      for (let i = 0; i < calendar.length; i += 1) {
        const date = calendar[i]
        const item = (this.limitUpByDate[date] || {})[code]
        const boards = item && Number(item.boards)
        if (boards > 0) run.push({ date, boards })
        else if (run.length) {
          streaks.push(run)
          run = []
        }
      }
      if (run.length) streaks.push(run)
      const indexOf = {}
      for (let i = 0; i < dates.length; i += 1) indexOf[dates[i]] = i
      const values = dates.map(() => null)
      const tails = dates.map(() => null)
      const premiums = []
      const put = (date, value) => {
        const index = indexOf[date]
        if (index == null || values[index] != null) return
        values[index] = value
      }
      const pct = this.firstBoardPct(code) / 100
      const drawn = []
      for (let s = 0; s < streaks.length; s += 1) {
        const streak = streaks[s]
        const height = streak.reduce((max, day) => Math.max(max, day.boards), 0)
        if (!free && height < 3) continue
        if (!free && (!afterDate || streak[0].date <= afterDate)) continue
        drawn.push(s)
        const counted = Math.min(height, streak.length)
        for (let k = 0; k < counted; k += 1) {
          put(streak[k].date, Math.round((Math.pow(1 + pct, k + 1) - 1) * 1000) / 10)
        }
        const last = streak[counted - 1]
        const lastPos = calendar.indexOf(last.date)
        const nextDate = calendar[lastPos + 1]
        const lastClose = Number(closes[last.date])
        const nextOpen = nextDate ? Number(opens[nextDate]) : NaN
        const nextClose = nextDate ? Number(closes[nextDate]) : NaN
        const nextIndex = nextDate ? indexOf[nextDate] : null
        if (nextIndex != null && lastClose > 0 && nextClose > 0) {
          premiums.push({
            index: nextIndex,
            height,
            openPct: nextOpen > 0 ? Math.round((nextOpen / lastClose - 1) * 10000) / 100 : null,
            closePct: Math.round((nextClose / lastClose - 1) * 10000) / 100
          })
        }
      }
      drawn.forEach((s, order) => {
        const streak = streaks[s]
        const height = streak.reduce((max, day) => Math.max(max, day.boards), 0)
        const counted = Math.min(height, streak.length)
        const last = streak[counted - 1]
        const lastPos = calendar.indexOf(last.date)
        const lastClose = Number(closes[last.date])
        const firstPos = calendar.indexOf(streak[0].date)
        const prevClose = firstPos > 0 ? Number(closes[calendar[firstPos - 1]]) : NaN
        const base = lastClose > 0 ? lastClose / Math.pow(1 + pct, counted) : prevClose
        const nextDate = calendar[lastPos + 1]
        let anchor = last.date
        if (nextDate && indexOf[nextDate] != null && values[indexOf[nextDate]] != null) anchor = nextDate
        const anchorPos = calendar.indexOf(anchor)
        const anchorIndex = indexOf[anchor]
        if (anchorIndex != null && values[anchorIndex] != null && tails[anchorIndex] == null) tails[anchorIndex] = values[anchorIndex]
        let stopPos = calendar.length
        const later = drawn[order + 1]
        if (later != null && base > 0) {
          const laterPos = calendar.indexOf(streaks[later][0].date)
          const closeBefore = laterPos > 0 ? Number(closes[calendar[laterPos - 1]]) : NaN
          const stillUp = closeBefore > 0 && (closeBefore / base - 1) >= pct
          if (laterPos > 0 && !stillUp) stopPos = laterPos
        }
        if (!(base > 0) || anchorPos < 0) return
        for (let p = anchorPos + 1; p < stopPos; p += 1) {
          const date = calendar[p]
          const index = indexOf[date]
          if (index == null || tails[index] != null) continue
          const close = Number(closes[date])
          if (!(close > 0)) continue
          tails[index] = Math.round((close / base - 1) * 1000) / 10
        }
      })
      return { values, tails, premiums }
    },
    collectKingdomPeers(dates, names) {
      const kingCode = this.kingdomKing ? this.kingdomKing.code : ''
      const peers = new Map()
      dates.forEach((date, index) => {
        this.limitUpItems(date).forEach((item) => {
          if (!item.code || item.code === kingCode) return
          if (this.hiddenCodeSet(kingCode)[item.code]) return
          const concepts = (this.conceptsByCode && this.conceptsByCode[item.code]) || []
          if (!names.some((name) => concepts.indexOf(name) >= 0)) return
          let peer = peers.get(item.code)
          if (!peer) {
            peer = {
              code: item.code,
              name: item.name || item.code,
              boards: dates.map(() => 0)
            }
            peers.set(item.code, peer)
          }
          if (item.name) peer.name = item.name
          peer.boards[index] = item.boards
        })
      })
      return Array.from(peers.values())
        .filter((peer) => peer.boards.some((boards) => boards > 0))
        .sort((a, b) => {
          const maxA = a.boards.reduce((max, boards) => Math.max(max, boards), 0)
          const maxB = b.boards.reduce((max, boards) => Math.max(max, boards), 0)
          return maxB - maxA || String(a.name).localeCompare(String(b.name), 'zh')
        })
        .map((peer, index) => ({ ...peer, color: this.peerColor(index) }))
    },
    selectDay(dayIndex) {
      this.activeDay = dayIndex
    },
    onRowClick(row) {
      this.selectStock(row.code)
    },
    onNodeClick(row) {
      this.selectDay(row.dayIndex)
    },
    rowClass({ row }) {
      return row.code === this.activeCode ? 'is-active-row' : ''
    },
    nodeRowClass({ row }) {
      return row.dayIndex === this.activeDay ? 'is-active-row' : ''
    },
    onSort({ prop, order }) {
      this.sortProp = order ? prop : ''
      this.sortOrder = order || ''
      this.page = 1
    },
    onPageSize(size) {
      this.pageSize = size
      this.page = 1
    },
    togglePoolPick(code) {
      const list = this.poolPicked.slice()
      const index = list.indexOf(code)
      if (index >= 0) list.splice(index, 1)
      else list.push(code)
      this.poolPicked = list
    },
    poolNameTag(row, member) {
      const code = String((row && row.code) || '').split('.')[0]
      const chars = Array.from(String((row && row.name) || '').trim())
      const homes = (member && member[code]) || []
      return {
        code,
        color: (row && row.color) || '#909399',
        name: String((row && row.name) || code),
        short: chars.slice(0, 2).join('') || code.slice(0, 2),
        star: homes.length >= 2
      }
    },
    openPoolMatch() {
      if (!this.poolPicked.length) return
      this.poolKingdomTabs = this.readKingdomTabs()
      this.poolMatchOpen = true
    },
    readKingdomTabs() {
      try {
        const raw = JSON.parse(localStorage.getItem('lcc.kingdom.tabs.v1') || '[]')
        if (!Array.isArray(raw)) return []
        return raw.filter((item) => item && item.id && item.name)
      } catch (err) {
        return []
      }
    },
    poolConceptBundle(hit) {
      const local = hit && hit.localConcepts
      const wc = [...new Set(resolveAnalysisConcepts(hit && hit.concepts, [], CONCEPT_SOURCE_THS))]
      const kpl = [...new Set(resolveAnalysisConcepts([], hit && hit.kplConcepts, CONCEPT_SOURCE_KPL))]
      const fixed = [...new Set(resolveAnalysisConcepts([], [], CONCEPT_SOURCE_THS, local))]
      const seen = {}
      const items = []
      const push = (name, source) => {
        if (!name || seen[name]) return
        seen[name] = true
        items.push({ name, source })
      }
      for (let i = 0; i < wc.length; i += 1) push(wc[i], 'WC')
      for (let i = 0; i < kpl.length; i += 1) push(kpl[i], 'KPL')
      for (let i = 0; i < fixed.length; i += 1) push(fixed[i], '固定')
      const merged = resolveAnalysisConcepts(hit && hit.concepts, hit && hit.kplConcepts, CONCEPT_SOURCE_MERGE, local)
      return { items, merged }
    },
    async ensurePoolLeaderConcepts(leader) {
      const code = String((leader && (leader.code || leader.leaderId)) || '').split('.')[0]
      if (!/^\d{6}$/.test(code)) return
      if (Object.prototype.hasOwnProperty.call(this.poolConceptItems, code)) return
      if (this.poolConceptPending[code]) return
      this.$set(this.poolConceptPending, code, true)
      try {
        const res = await stockApi.searchStocks(code)
        const data = (res && res.data) || res || []
        const rows = Array.isArray(data) ? data : []
        let hit = null
        for (let i = 0; i < rows.length; i += 1) {
          const rowCode = String(rows[i].code || rows[i].ts_code || '').split('.')[0]
          if (rowCode === code) hit = rows[i]
        }
        const bundle = this.poolConceptBundle(hit || {})
        this.$set(this.poolConceptItems, code, bundle.items)
        this.$set(this.poolConceptNames, code, bundle.merged)
      } catch (err) {
        this.$set(this.poolConceptItems, code, [])
        this.$set(this.poolConceptNames, code, [])
      } finally {
        this.$delete(this.poolConceptPending, code)
      }
    },
    async onPoolCreateKingdom(payload) {
      if (this.poolMatchSaving) return
      const members = (payload && payload.members) || []
      let leader = null
      for (let i = 0; i < members.length; i += 1) {
        if (members[i] && members[i].role === 'leader') leader = members[i]
      }
      const code = String((leader && leader.code) || '').split('.')[0]
      const tradeDate = String(this.tradeDate || '')
      if (!/^\d{6}$/.test(code)) {
        this.$message.warning('请把其中一只标成总龙')
        return
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) {
        this.$message.warning('先选择交易日')
        return
      }
      const concepts = []
      if (payload.mainConceptName) concepts.push(payload.mainConceptName)
      const subs = payload.subConceptNames || []
      for (let i = 0; i < subs.length; i += 1) {
        if (subs[i] && concepts.indexOf(subs[i]) < 0) concepts.push(subs[i])
      }
      const peers = []
      for (let i = 0; i < members.length; i += 1) {
        const peer = String((members[i] && members[i].code) || '').split('.')[0]
        if (!/^\d{6}$/.test(peer) || peer === code) continue
        peers.push({ code: peer, name: (members[i] && members[i].name) || peer })
      }
      this.poolMatchSaving = true
      try {
        await stockApi.saveCycleLineConcept({
          code,
          concepts,
          match: 'or',
          kingdomTitle: payload.name
        })
        if (peers.length) {
          await stockApi.saveCycleLineMembers({
            code,
            tradeDate,
            members: peers,
            removed: []
          })
        }
        const tabs = this.readKingdomTabs()
        let index = -1
        for (let i = 0; i < tabs.length; i += 1) {
          if (tabs[i].id === payload.id || tabs[i].name === payload.name) index = i
        }
        const keptId = index >= 0 ? tabs[index].id : payload.id
        const row = Object.assign({}, payload, { id: keptId })
        if (index >= 0) tabs.splice(index, 1, row)
        else tabs.push(row)
        persistKingdomTabs(tabs)
        this.poolKingdomTabs = tabs
        this.poolMatchOpen = false
        this.poolPicked = []
        await this.loadSavedConcepts()
        this.$message.success('已生成王国 ' + payload.name)
      } catch (err) {
        this.$message.error((err && err.message) || '王国保存失败')
      } finally {
        this.poolMatchSaving = false
      }
    },
    toggleSide() {
      this.sideOpen = !this.sideOpen
      this.$nextTick(() => {
        if (this.$refs.charts && this.$refs.charts.resize) this.$refs.charts.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.cycle-page {
  color: #303133;
}
.toolbar,
.table-card,
.side-card {
  background: #fff;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
}
.toolbar { padding: 12px 14px 10px; margin-bottom: 10px; }
.toolbar__grid {
  display: grid;
  grid-template-columns: 168px auto;
  grid-template-areas:
    "picker ranges"
    "nav concept";
  column-gap: 16px;
  row-gap: 6px;
  align-items: center;
}
.date-block { grid-area: picker; }
.range-stack { grid-area: ranges; align-self: end; }
.date-nav { grid-area: nav; }
.toolbar__concept { grid-area: concept; justify-self: start; }
.toolbar__title { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.toolbar__name { font-size: 18px; font-weight: 700; color: #1f4e79; }
.toolbar__sub { margin-top: 2px; color: #7d8da3; font-size: 12px; }
.toolbar__row,
.manual-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-top: 10px;
}
.date-field { align-items: flex-start; }
.date-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 168px;
  ::v-deep .el-date-editor.el-input {
    width: 168px;
  }
}
.date-nav {
  display: flex;
  gap: 4px;
  width: 168px;
  button {
    flex: 1;
    height: 24px;
    border: 1px solid #d7e4f6;
    background: #f7faff;
    color: #1f4e79;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    &:disabled { color: #c0c4cc; cursor: not-allowed; }
  }
}
.field-select { width: 132px; }
.checks { gap: 8px; }
.toolbar__concept {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  > span { color: #606266; font-size: 12px; white-space: nowrap; }
}
.manual-select { width: 280px; }
.range-stack {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}
.range-stack .field {
  display: flex;
  align-items: center;
}
.range-stack .field span {
  width: auto;
  margin-right: 6px;
  white-space: nowrap;
}
.range-stack .is-driving span {
  color: #1f4e79;
  font-weight: 700;
}
.seg-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.match-under {
  margin-top: 4px;
  margin-right: 11px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f4e79;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
}
.match-under:disabled {
  color: #c0c4cc;
  cursor: default;
}
.seg {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 52px);
  padding: 2px;
  background: #f4f8fc;
  border: 1px solid #d7e4f6;
  border-radius: 4px;
}
.seg__thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  width: 52px;
  border-radius: 3px;
  background: #409eff;
  box-shadow: 0 1px 4px rgba(64, 158, 255, 0.35);
  pointer-events: none;
  z-index: 0;
  transition: transform 0.28s cubic-bezier(0.22, 0.8, 0.28, 1), opacity 0.2s ease;
}
.seg button {
  position: relative;
  z-index: 1;
  height: 22px;
  border: 0;
  background: transparent;
  color: #606266;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.12s ease;
}
.seg button:hover { color: #1f4e79; }
.seg button:active { transform: scale(0.92); }
.seg button.is-active,
.seg button.is-active:hover {
  color: #fff;
  font-weight: 700;
}
.demo-note { margin-top: 8px; color: #b88230; font-size: 12px; }
.cycle-body { display: flex; align-items: flex-start; gap: 10px; }
.cycle-main { width: 68%; min-width: 0; }
.cycle-main.is-full { width: 100%; }
.finals-card {
  background: #fff;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.finals-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
}
.finals-date { color: #909399; font-size: 12px; }
.finals-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
}
.final-item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
  color: #303133;
  line-height: 1.6;
}
.final-name { font-weight: 700; color: #1f4e79; }
.final-sep { color: #c0c4cc; }
.final-star { color: #e6a23c; font-weight: 700; }
.cycle-side { width: 32%; min-width: 280px; }
.side-toggle,
.side-reopen {
  border: 1px solid #d7e6f5;
  background: #f7faff;
  color: #1f4e79;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  height: 28px;
  padding: 0 10px;
}
.side-reopen {
  position: sticky;
  top: 8px;
  align-self: flex-start;
  writing-mode: vertical-rl;
  height: auto;
  padding: 12px 6px;
  letter-spacing: 2px;
}
.concept-save {
  display: flex;
  align-items: center;
  gap: 8px;
  .el-select { width: 168px; }
  .el-button { flex: none; margin: 0; }
}
.concept-match {
  flex: none;
}
.metric__label { color: #909399; font-size: 12px; margin-bottom: 2px; }
.metric__return { color: #f25c54; font-size: 26px; font-weight: 700; line-height: 1.1; }
.metric__num { font-size: 18px; font-weight: 700; color: #303133; }
.metric__num--blue { color: #409eff; }
.metric__num--red { color: #e85d4c; }
.table-card { padding: 10px; }
.table-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.table-title, .side-title { font-size: 14px; font-weight: 700; color: #1f4e79; }
.role-filter { width: 120px; }
.table-note { margin-left: auto; color: #b88230; font-size: 12px; }
.pager { display: flex; justify-content: flex-end; margin-top: 8px; }
.up { color: #f25c54; font-weight: 700; }
.side-card { padding: 10px; margin-bottom: 10px; }
.side-head { display: flex; justify-content: space-between; align-items: flex-start; }
.side-head i { cursor: pointer; color: #909399; }
.side-sub { color: #909399; font-size: 12px; margin-top: 2px; }
.diag-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 8px;
  margin: 8px 0;
  font-size: 12px;
  color: #909399;
  b { display: block; font-size: 16px; }
  .blue { color: #409eff; }
  .red { color: #e85d4c; }
}
.detail-tabs { display: flex; margin-bottom: 8px; }
.judge-grid { display: grid; grid-template-columns: 1fr 1fr 0.8fr; gap: 6px; }
.judge {
  background: #f7fbff;
  border: 1px solid #e3eef8;
  border-radius: 6px;
  padding: 6px;
  font-size: 12px;
  color: #606266;
  ul { margin: 4px 0 0; padding-left: 16px; }
  p { margin: 6px 0 0; }
}
.judge__title { font-weight: 700; color: #1f4e79; }
.judge__title--orange { color: #e6a23c; }
.judge__title--red { color: #f25c54; }
.detail-list { max-height: 180px; overflow: auto; }
.detail-item {
  padding: 6px 0;
  border-bottom: 1px dashed #e3eef8;
  font-size: 12px;
  cursor: pointer;
  p { margin: 2px 0 0; color: #909399; }
}
.base-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  font-size: 12px;
  span { display: block; color: #909399; }
}
.legend-row {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  font-size: 12px;
  color: #606266;
  margin-top: 6px;
  b { white-space: nowrap; }
}
.usage { margin: 6px 0 0; padding-left: 18px; color: #606266; font-size: 12px; line-height: 1.55; }
.stage-text--rise { color: #409eff; }
.stage-text--expand { color: #67c23a; }
.stage-text--stall { color: #e6a23c; }
.stage-text--age { color: #f25c54; }
.empty { color: #c0c4cc; font-size: 12px; }
::v-deep .is-active-row td { background: #eaf4ff !important; }
@media (max-width: 1200px) {
  .cycle-body { display: block; }
  .cycle-main, .cycle-side { width: 100%; }
}
</style>

<style lang="scss">
.kingdom-dialog.el-dialog {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 4vh);
  margin-bottom: 2vh;
}
.kingdom-dialog .el-dialog__title {
  display: inline-block;
  border: 1px solid #409eff;
  background: #ecf5ff;
  color: #1f4e79;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}
.kingdom-dialog .el-dialog__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.kingdom-gallery-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.kingdom-gallery {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
}
.kingdom-gallery__note {
  padding: 24px 8px;
  color: #909399;
  font-size: 14px;
}
.kingdom-focus {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}
.kingdom-back {
  align-self: flex-start;
  margin: 0 0 8px;
  border: 1px solid #d7e6f5;
  background: #fff;
  color: #1f4e79;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
}
</style>

<style lang="scss">
.kingdom-save__label { margin-bottom: 8px; color: #606266; }
.kingdom-save__box { margin-top: 12px; }
.kingdom-save__box .kingdom-save__label b { margin-left: 6px; color: #1f4e79; }
.kingdom-save__list { max-height: 180px; overflow: auto; border: 1px solid #e4e7ed; border-radius: 6px; }
.kingdom-save__branch {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  .el-radio {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 7px 10px;
    border-bottom: 1px solid #f2f3f5;
  }
  .el-radio.is-checked { background: #ecf5ff; }
  .el-radio__label {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  em { color: #f25c54; font-style: normal; }
}
.kingdom-save__empty { padding: 12px; color: #909399; }
.pool-match-dialog {
  margin-bottom: 0;
  .el-dialog__body {
    max-height: calc(100vh - 72px);
    overflow: auto;
    padding: 8px 12px 16px;
  }
}
</style>
